import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import usmApi, { USM_BASE, USM_TOKEN_KEY } from "@/lib/usmApi";
import api from "@/lib/api";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { useUsmPrefs } from "@/contexts/UsmPrefsContext";
import { PageHeader } from "../UsmLayout";
import Can from "@/components/usm/Can";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, RefreshCw, History, Barcode, FileSpreadsheet, Printer, Columns3, GripVertical, RotateCcw, Lock, Unlock, DollarSign, X as XIcon } from "lucide-react";
import LabelPrintDialog from "./LabelPrintDialog";
import BatchLabelPrintDialog from "./BatchLabelPrintDialog";
import InventorySummary from "./InventorySummary";
import { getEngineForMaterial } from "./pricingEngines";
import { CANONICAL_CATEGORIES_METAL, buildSlugToIds, buildIdToSlug, pickBestCategoryId, resolveCanonicalSlug, buildWatchCategoriesFromBrands } from "./categoryNormalizer";
import { usePersistedState } from "@/hooks/usePersistedState";
import { cachedGet } from "@/lib/inventoryCache";
import ScannerButton from "@/components/usm/ScannerButton";

// Phase 46 — Feature #9. Canonical customizable column list.
// The leading checkbox and trailing actions column are fixed (not user-configurable).
const CUSTOMIZABLE_COLUMNS = [
  { key: "photo",        label: "Görsel",        defaultWidth: 72,  align: "left"  },
  { key: "stock_code",   label: "Stok Kodu",     defaultWidth: 130, align: "left"  },
  { key: "barcode",      label: "Barkod",        defaultWidth: 120, align: "left"  },
  { key: "name",         label: "Ürün",          defaultWidth: 220, align: "left"  },
  { key: "branch",       label: "Şube",          defaultWidth: 160, align: "left"  },
  // Phase 54.25 #4 — Ring Size column, positioned immediately before
  // "Ayar / Gr". Shows the ring_size number when the item's category is
  // a ring; otherwise renders "—". No filtering / calculation impact.
  { key: "ring_size",    label: "Yüzük Ölçüsü",  defaultWidth: 96,  align: "right" },
  { key: "material",     label: "Ayar / Gr",     defaultWidth: 130, align: "left"  },
  { key: "price",        label: "Alış Fiyatı",   defaultWidth: 160, align: "right" },
  { key: "quantity",     label: "Adet",          defaultWidth: 70,  align: "right" },
  { key: "min_quantity", label: "Min",           defaultWidth: 60,  align: "right" },
  { key: "stock_state",  label: "Stok",          defaultWidth: 110, align: "left"  },
  { key: "status",       label: "Durum",         defaultWidth: 110, align: "left"  },
];
const COLUMN_LABELS = Object.fromEntries(CUSTOMIZABLE_COLUMNS.map((c) => [c.key, c.label]));
const COLUMN_META  = Object.fromEntries(CUSTOMIZABLE_COLUMNS.map((c) => [c.key, c]));

// Phase 47.2 #1 — Standardized Material / Ayar values. The backend migration
// normalizes all legacy free-text into one of these. Kept in code (not a DB
// collection) because the set is tiny, semantic, and never customer-editable.
export const MATERIAL_OPTIONS = [
  "8K GOLD",
  "14K GOLD",
  "18K GOLD",
  "22K GOLD",
  "24K GOLD",
  "925 SILVER",
  "999 SILVER",
  // Phase 53.6 — Watch Inventory workspace. Watches reuse the same inventory
  // engine as gold/silver; only the material tokens differ so the scope
  // filter can carve out a dedicated workspace.
  "STEEL WATCH",
  "GOLD WATCH",
  "SMART WATCH",
  "OTHER WATCH",
];

const STATUS = [
  { value: "active",    label: "Aktif",   cls: "text-emerald-700" },
  { value: "reserved",  label: "Rezerve", cls: "text-amber-700" },
  { value: "sold",      label: "Satıldı", cls: "text-blue-700" },
  { value: "repair",    label: "Tamirde", cls: "text-purple-700" },
  { value: "transfer",  label: "Transferde", cls: "text-cyan-700" },
  { value: "lost",      label: "Kayıp", cls: "text-red-700" },
];
const STATUS_LABEL = Object.fromEntries(STATUS.map((s) => [s.value, s]));

const GENDERS = [
  { value: "kadin",  label: "Kadın" },
  { value: "erkek",  label: "Erkek" },
  { value: "unisex", label: "Unisex" },
  { value: "cocuk",  label: "Çocuk" },
];

const EMPTY = {
  barcode: "", stock_code: "", sku: "", name: "", branch_id: "", category_id: "",
  material: "", purity: "", weight_grams: "", gender: "", ring_size: "", photo_urls: [],
  quantity: 1, min_quantity: 0,
  purchase_price_try: "", sale_price_try: "", status: "active",
  status_reason: "", notes: "", label_note: "",  // Phase 46 — Feature #3
  brand_id: "",  // Phase 53.3 — Gold Brand Engine
  // Phase 54.22 — Gold sub-category (14K/18K/22K/SARRAFIYE/HURDA). Empty on
  // create; required in the editor when material is gold. Legacy rows keep
  // whatever value is on the server.
  gold_category: "",
  gold_buy_multiplier: "",
  gold_sell_multiplier: "",
};

const GOLD_MATERIALS_SET = new Set(["8K GOLD", "14K GOLD", "18K GOLD", "22K GOLD", "24K GOLD"]);

// Phase 54.22 — Gold Inventory sub-categories. Order defines chip strip
// display order in the sidebar filter.
const GOLD_CATEGORIES = [
  { value: "14K",       label: "14 Ayar" },
  { value: "18K",       label: "18 Ayar" },
  { value: "22K",       label: "22 Ayar" },
  { value: "SARRAFIYE", label: "Sarrafiye" },
  { value: "HURDA",     label: "Hurda" },
];

const FMT = {
  TRY: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 }),
  USD: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "USD", minimumFractionDigits: 2 }),
  EUR: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }),
  AED: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "AED", minimumFractionDigits: 2 }),
  SAR: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "SAR", minimumFractionDigits: 2 }),
};

// Heuristic: is this category a ring? Used to gate the ring_size input.
function isRingCategory(cat) {
  if (!cat) return false;
  const slug = (cat.slug || "").toLowerCase();
  const tr = (cat.name_tr || "").toLowerCase();
  if (["yuzuk", "ring", "rings", "yuzukler", "yüzük", "yüzükler"].includes(slug)) return true;
  return tr.includes("yüz") || tr.includes("yuz") || tr.includes("ring");
}

export default function InventoryList({ scope }) {
  const { hasPerm, isSuperAdmin } = useUsmAuth();
  const { prefs, ready: prefsReady, addFilterPreset, deleteFilterPreset, setInventoryColumns, setActiveBranch } = useUsmPrefs();  // Phase 46
  const canCost = hasPerm("pricing.view_cost");
  // Phase 52.1 — Silver/Gold split. When `scope` is set, the material filter
  // dropdown is restricted to the whitelist and every API call carries a
  // `materials` param so both the list and summary aggregation stay scoped.
  const scopeMaterials = useMemo(() => {
    if (scope === "silver") return ["925 SILVER", "999 SILVER"];
    if (scope === "gold") return ["8K GOLD", "14K GOLD", "18K GOLD", "22K GOLD", "24K GOLD"];
    if (scope === "watch") return ["STEEL WATCH", "GOLD WATCH", "SMART WATCH", "OTHER WATCH"];
    return null;
  }, [scope]);
  const scopeTitle = scope === "gold" ? "Altın Envanteri" : scope === "silver" ? "Gümüş Envanteri" : scope === "watch" ? "Saat Envanteri" : "Envanter";
  const materialOptionsForScope = useMemo(
    () => (scopeMaterials ? MATERIAL_OPTIONS.filter((m) => scopeMaterials.includes(m)) : MATERIAL_OPTIONS),
    [scopeMaterials],
  );
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // Phase 54.7.A — persist page + filter state per scope so the operator
  // can navigate away (dashboard, sale detail, etc.) and return to the
  // exact page/filters they left. Key is `inventory.<scope>` to keep
  // Silver/Gold/Watch/All strictly isolated.
  const persistKey = `inventory.${scope || "all"}`;
  const [page, setPage] = usePersistedState(`${persistKey}.page`, 1);
  const limit = 50;
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  // Phase 46 — Feature #7. Advanced multi-facet filters.
  const [filter, setFilter] = usePersistedState(`${persistKey}.filter`, {
    q: "", branch_id: "", status: "", category_id: "",
    // Phase 53.8 — canonical category bucket. When set, expands to all
    // legacy category_ids that normalize to this slug.
    category_slug: "",
    // Phase 54.2 — Watch scope filter by dynamic Brand-as-category.
    brand_id: "",
    material: "", purity: "",
    min_weight: "", max_weight: "",
    min_price: "", max_price: "",
    stock_code: "", ean8: "", label_note: "",
    has_price: "", has_image: "",
    // Phase 53.7 — Negative-Stock quick filter (controlled oversell).
    negative_only: false,
    // Phase 54.22 — Gold sub-category filter (Gold Inventory only).
    // "" (empty) = all categories.
    gold_category: "",
  });
  // Phase 54.24 — Local search input state, debounced (300 ms) into
  // filter.q. Every keystroke used to fire a /inventory GET; now only
  // fires after the user pauses typing. Preserves same on-screen
  // responsiveness of the text input.
  const [qInput, setQInput] = useState(filter.q || "");
  useEffect(() => {
    if (qInput === filter.q) return;
    const h = setTimeout(() => {
      setFilter((p) => ({ ...p, q: qInput }));
    }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);
  const [showAdv, setShowAdv] = useState(false);
  // Phase 46 — Feature #6. Sticky per-user branch is the single source of truth.
  //
  // BUG FIX (Phase 46.1): The previous effect had `f.branch_id ? f : {...}`
  // which only wrote the filter on FIRST load (when filter.branch_id was
  // empty). After that, changing the sidebar BranchSwitcher would update
  // `prefs.active_branch_id` but silently no-op on filter → the inventory
  // list would not re-fetch. Toolbar dropdown and sidebar drifted apart.
  //
  // Fix: always sync filter.branch_id ← prefs.active_branch_id for admins.
  // For non-admins, we keep the initial-only behaviour (they usually only
  // see their own assigned branch and use the toolbar dropdown for local
  // filtering).
  useEffect(() => {
    // Phase 54.7.A — do NOT sync while prefs are still loading. During
    // that window `prefs.active_branch_id` is `null`, which would blow
    // away a persisted branch filter and trigger the "filter changed"
    // pathway that resets pagination. Wait until the context is ready.
    if (!prefsReady) return;
    if (!isSuperAdmin) {
      if (prefs?.active_branch_id) {
        setFilter((f) => f.branch_id ? f : { ...f, branch_id: prefs.active_branch_id });
      }
      return;
    }
    const bid = prefs?.active_branch_id || "";
    setFilter((f) => f.branch_id === bid ? f : { ...f, branch_id: bid });
  }, [prefsReady, prefs?.active_branch_id, isSuperAdmin]);

  // Phase 52.1 — Clear a stale material filter (e.g. "925 SILVER" persisted
  // from the Silver page) when the user switches to a scope that doesn't
  // allow it. Without this the backend's single-`material` clause would
  // shadow the scope's `materials` whitelist and show the wrong metal.
  useEffect(() => {
    if (!scopeMaterials) return;
    setFilter((f) => (
      f.material && !scopeMaterials.includes(f.material)
        ? { ...f, material: "" }
        : f
    ));
  }, [scopeMaterials]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [labelFor, setLabelFor] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // Set<string>
  const [batchOpen, setBatchOpen] = useState(false);
  // Phase 53.8 — Bulk Price Update modal.
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  // Phase 47 #9 — column customization popover; Phase 50 #7 — image lightbox.
  const [showColsPicker, setShowColsPicker] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { url, name } | null

  // Merge stored prefs with defaults so newly-added columns always appear
  // (visible by default) even for users who saved prefs before that column existed.
  const effectiveColumns = useMemo(() => {
    const stored = Array.isArray(prefs?.inventory_columns) ? prefs.inventory_columns : [];
    const seen = new Set();
    const merged = [];
    for (const c of stored) {
      if (!COLUMN_META[c.key] || seen.has(c.key)) continue;
      seen.add(c.key);
      merged.push({
        key: c.key,
        visible: c.visible !== false,
        width: typeof c.width === "number" ? c.width : COLUMN_META[c.key].defaultWidth,
      });
    }
    for (const def of CUSTOMIZABLE_COLUMNS) {
      if (!seen.has(def.key)) {
        merged.push({ key: def.key, visible: true, width: def.defaultWidth });
      }
    }
    return merged;
  }, [prefs?.inventory_columns]);
  const visibleColumns = useMemo(() => effectiveColumns.filter((c) => c.visible), [effectiveColumns]);

  const persistColumns = (next) => setInventoryColumns(next);
  const toggleColumn = (key) => {
    persistColumns(effectiveColumns.map((c) => c.key === key ? { ...c, visible: !c.visible } : c));
  };
  const moveColumn = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= effectiveColumns.length || fromIdx === toIdx) return;
    const next = effectiveColumns.slice();
    const [it] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, it);
    persistColumns(next);
  };
  // Feature #9 resize — during a drag we update local state only (via a ref for
  // latest snapshot to avoid stale-closure), and commit ONE PATCH on pointerup.
  // This avoids emitting dozens of intermediate PATCH /prefs/me requests per drag.
  const [dragWidths, setDragWidths] = useState(null); // { key: width } or null when not dragging
  const dragWidthsRef = React.useRef(null);
  const setColumnWidth = (key, width) => {
    const clamped = Math.max(50, Math.min(800, width));
    const next = { ...(dragWidthsRef.current || {}), [key]: clamped };
    dragWidthsRef.current = next;
    setDragWidths(next);
  };
  const commitColumnWidths = () => {
    const current = dragWidthsRef.current;
    if (!current) return;
    const next = effectiveColumns.map((c) => current[c.key] != null ? { ...c, width: current[c.key] } : c);
    dragWidthsRef.current = null;
    setDragWidths(null);
    persistColumns(next);
  };
  // Apply drag overrides on top of effectiveColumns for rendering.
  const columnsForRender = useMemo(() => (
    dragWidths
      ? effectiveColumns.map((c) => dragWidths[c.key] != null ? { ...c, width: dragWidths[c.key] } : c)
      : effectiveColumns
  ), [effectiveColumns, dragWidths]);
  const visibleColumnsForRender = useMemo(() => columnsForRender.filter((c) => c.visible), [columnsForRender]);
  const resetColumns = () => {
    persistColumns(CUSTOMIZABLE_COLUMNS.map((c) => ({ key: c.key, visible: true, width: c.defaultWidth })));
  };

  const downloadBarcode = async (it) => {
    try {
      const token = localStorage.getItem(USM_TOKEN_KEY);
      const r = await fetch(`${USM_BASE}/inventory/${it.id}/barcode.png?download=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Barkod oluşturulamadı");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${it.stock_code || it.barcode}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Barkod indirildi");
    } catch (e) { toast.error(e.message || "Barkod indirilemedi"); }
  };

  // BUG FIX (Phase 46.1): stale-response guard. Rapid branch switches or
  // filter changes issue overlapping GET /inventory requests; without a
  // guard, whichever response arrives LAST wins — including responses to
  // now-obsolete queries. That was the cause of "products disappear / only
  // one product shows / F5 fixes it" reports. Each call bumps a monotonic
  // request id; when a response returns, we drop it if a newer request has
  // been fired in the meantime.
  const loadReqIdRef = React.useRef(0);
  // Phase 51 — Bump on every successful load(), Create, Edit, Delete so the
  // <InventorySummary /> re-aggregates in sync. Cheap number → no expensive
  // ref-equality churn for the child.
  const [summaryVersion, setSummaryVersion] = useState(0);
  // Phase 54.24 — AbortController on the /inventory GET so rapid filter
  // changes cancel the in-flight bytes instead of just discarding them
  // client-side. Saves bandwidth AND backend CPU. Belt-and-braces: we
  // keep the `reqId` monotonic guard so a race between abort and
  // arrival still can't overwrite fresh state with stale data.
  const loadAbortRef = React.useRef(null);
  const load = async (p = page) => {
    const reqId = ++loadReqIdRef.current;
    // Cancel any in-flight /inventory GET before firing the new one.
    if (loadAbortRef.current) {
      try { loadAbortRef.current.abort(); } catch { /* noop */ }
    }
    const ac = new AbortController();
    loadAbortRef.current = ac;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Phase 46 — Every filter dimension shipped as a query param. Backend
      // ANDs them together.
      Object.entries(filter).forEach(([k, v]) => {
        // Phase 53.7 — Skip booleans equal to false so we don't spuriously
        // serialize `negative_only=false` to the server.
        if (v === false) return;
        // Phase 53.8 — `category_slug` is a client-side concept; expand it
        // into `category_ids=id1,id2,…` for the server. Never forward the
        // slug itself.
        if (k === "category_slug") {
          if (v && slugToIds[v]?.length) {
            params.set("category_ids", slugToIds[v].join(","));
          }
          return;
        }
        if (v !== "" && v !== null && v !== undefined) params.set(k, String(v));
      });
      params.set("page", p);
      params.set("limit", limit);
      // Phase 52.1 — Silver/Gold scope enforcement. Always overrides any
      // stale material filter that might have been persisted from the
      // "all materials" view.
      if (scopeMaterials) params.set("materials", scopeMaterials.join(","));
      const r = await usmApi.get(`/inventory?${params.toString()}`, { signal: ac.signal });
      if (reqId !== loadReqIdRef.current) return; // stale response — a newer load() is already in-flight
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSummaryVersion((v) => v + 1);
    } catch (e) {
      // Aborted → new load in flight; silently drop.
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError") return;
      if (reqId !== loadReqIdRef.current) return;
      toast.error("Envanter yüklenemedi");
    }
    finally {
      if (reqId === loadReqIdRef.current) setLoading(false);
    }
  };

  // Phase 46 — Feature #9. Preset save/apply helpers.
  const applyPreset = (preset) => {
    setFilter({ ...({ q: "", branch_id: "", status: "", category_id: "", category_slug: "", brand_id: "", material: "", purity: "", min_weight: "", max_weight: "", min_price: "", max_price: "", stock_code: "", ean8: "", label_note: "", has_price: "", has_image: "", negative_only: false, gold_category: "" }), ...(preset.filters || {}) });
    setPage(1);
  };
  const saveCurrentAsPreset = async () => {
    const name = prompt("Filtre kayıt adı:");
    if (!name?.trim()) return;
    const nonEmpty = Object.fromEntries(Object.entries(filter).filter(([, v]) => v !== "" && v !== null));
    const r = await addFilterPreset(name.trim(), nonEmpty);
    if (r) toast.success(`Kaydedildi: ${r.name}`); else toast.error("Kaydedilemedi");
  };

  useEffect(() => {
    (async () => {
      try {
        // Phase 54.24 — cached (5-min TTL) so tab-hops between silver /
        // gold / watch inventories don't refetch these stable resources.
        const [b, c] = await Promise.all([
          cachedGet("usm:/branches", () => usmApi.get("/branches").then((r) => r.data || [])),
          cachedGet("public:/categories", () => api.get("/categories").then((r) => r.data || [])),
        ]);
        setBranches(b || []);
        setCategories(c || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);
  // Phase 54.7.A — On initial mount we must load the *persisted* page,
  // not reset to page 1. And subsequent branch-sync effects that don't
  // actually change any user-facing filter must not reset the page
  // either. Compare the current filter snapshot to the previous one
  // (stringified) — only reset when the effective filter really changes.
  const lastFilterRef = React.useRef(null);
  useEffect(() => {
    const snap = JSON.stringify([
      filter.q, filter.branch_id, filter.status, filter.category_id,
      filter.category_slug, filter.brand_id, filter.material, filter.purity,
      filter.min_weight, filter.max_weight, filter.min_price, filter.max_price,
      filter.stock_code, filter.ean8, filter.label_note,
      filter.has_price, filter.has_image, filter.negative_only,
      filter.gold_category,  // Phase 54.22 — Gold sub-category chip strip.
      scope,
    ]);
    if (lastFilterRef.current === null) {
      // First evaluation → honour the persisted page.
      lastFilterRef.current = snap;
      load(page);
      return;
    }
    if (lastFilterRef.current === snap) return;      // no-op filter tick (branch-sync)
    lastFilterRef.current = snap;
    setPage(1); load(1);
  }, [filter.q, filter.branch_id, filter.status, filter.category_id, filter.category_slug, filter.brand_id, filter.material, filter.purity, filter.min_weight, filter.max_weight, filter.min_price, filter.max_price, filter.stock_code, filter.ean8, filter.label_note, filter.has_price, filter.has_image, filter.negative_only, filter.gold_category, scope]);

  const branchById = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);
  const categoryById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  // Phase 53.8 — canonical bucket expansion (slug → array of legacy IDs).
  const slugToIds = useMemo(() => buildSlugToIds(categories), [categories]);
  const idToSlug  = useMemo(() => buildIdToSlug(categories),  [categories]);
  // Phase 54.4 — Gold Inventory optionally filters by dynamic Brand
  // (previously the Watch workspace used brands as categories; now Watch
  // is back to canonical buckets and Gold gets an optional Brand filter
  // beside Category).
  const [brandsForFilter, setBrandsForFilter] = useState([]);
  useEffect(() => {
    if (scope !== "gold") return;
    // Phase 54.24 — cached (5-min TTL); brands are ref data.
    cachedGet("usm:/accounting/brands?status=active",
              () => usmApi.get("/accounting/brands?status=active").then((r) => Array.isArray(r.data) ? r.data : []))
      .then((data) => setBrandsForFilter(data))
      .catch(() => setBrandsForFilter([]));
  }, [scope]);
  // Legacy — kept exported for future Watch-with-brands rollout.
  const watchCategoryOptions = useMemo(() => buildWatchCategoriesFromBrands(brandsForFilter), [brandsForFilter]);
  // Phase 38 — group inventory by stock_code so we can flag products that
  // hold stock in multiple branches simultaneously and show a quick total.
  const branchBreakdown = useMemo(() => {
    const m = new Map(); // stock_code → [{branch_name, branch_code, quantity}]
    items.forEach((it) => {
      const sc = it.stock_code || it.barcode;
      if (!sc) return;
      const arr = m.get(sc) || [];
      arr.push({
        branch_id: it.branch_id,
        branch_name: branchById[it.branch_id]?.name || "—",
        branch_code: branchById[it.branch_id]?.code,
        quantity: parseInt(it.quantity ?? 0, 10),
      });
      m.set(sc, arr);
    });
    return m;
  }, [items, branchById]);

  const onSave = async (data, mode, id, opts = {}) => {
    try {
      const payload = { ...data };
      payload.weight_grams = payload.weight_grams === "" ? null : Number(payload.weight_grams);
      payload.purchase_price_try = payload.purchase_price_try === "" ? null : Number(payload.purchase_price_try);
      // Phase 46 — sale_price stored as null when empty (was 0). Displays as "-".
      payload.sale_price_try = payload.sale_price_try === "" ? null : Number(payload.sale_price_try);
      payload.ring_size = payload.ring_size === "" ? null : Number(payload.ring_size);
      payload.quantity = payload.quantity === "" || payload.quantity == null ? 1 : Math.max(0, parseInt(payload.quantity, 10) || 0);
      payload.min_quantity = payload.min_quantity === "" || payload.min_quantity == null ? 0 : Math.max(0, parseInt(payload.min_quantity, 10) || 0);
      ["sku", "category_id", "material", "purity", "gender", "status_reason", "notes", "label_note", "product_id", "stock_code", "barcode", "brand_id"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      // Server hides ring_size if not a ring; but for non-ring categories we send null to clear it
      if (payload.category_id && !isRingCategory(categoryById[payload.category_id])) {
        payload.ring_size = null;
      }
      let saved;
      if (mode === "create") {
        const r = await usmApi.post("/inventory", payload);
        saved = r.data;
      } else {
        const r = await usmApi.patch(`/inventory/${id}`, payload);
        saved = r.data;
      }
      // Phase 47 #4 / 47.2 #3 / 48 #7 / 49 — remember last chosen branch, category, material, gender, silver USD/g, multiplier.
      if (mode === "create") {
        try {
          if (payload.branch_id) sessionStorage.setItem("usm_last_branch_id", payload.branch_id);
          if (payload.category_id) sessionStorage.setItem("usm_last_category_id", payload.category_id);
          if (payload.material) sessionStorage.setItem("usm_last_material", payload.material);
          if (payload.gender) sessionStorage.setItem("usm_last_gender", payload.gender);
          if (payload.silver_purchase_usd != null && payload.silver_purchase_usd !== "") {
            sessionStorage.setItem("usm_last_silver_purchase_usd", String(payload.silver_purchase_usd));
          }
          if (payload.pricing_multiplier != null && payload.pricing_multiplier !== "") {
            sessionStorage.setItem("usm_last_pricing_multiplier", String(payload.pricing_multiplier));
          }
          // Phase 54.22 — remember last gold buy/sell multipliers.
          if (payload.gold_buy_multiplier != null && payload.gold_buy_multiplier !== "") {
            sessionStorage.setItem("usm_last_gold_buy_multiplier", String(payload.gold_buy_multiplier));
          }
          if (payload.gold_sell_multiplier != null && payload.gold_sell_multiplier !== "") {
            sessionStorage.setItem("usm_last_gold_sell_multiplier", String(payload.gold_sell_multiplier));
          }
          // Phase 54.25 #2 — Speed up repetitive gold-inventory data entry
          // by remembering the last-used Brand and Gold Category. Applied
          // ONLY when the operator is in the Gold Inventory workspace
          // (scope === 'gold'); the fields don't exist for silver / watch
          // so scoping avoids spurious writes.
          if (scope === "gold") {
            if (payload.brand_id) sessionStorage.setItem("usm_last_gold_brand_id", payload.brand_id);
            else sessionStorage.removeItem("usm_last_gold_brand_id");
            if (payload.gold_category) sessionStorage.setItem("usm_last_gold_category", payload.gold_category);
          }
        } catch { /* private mode */ }
      }
      toast.success("Kaydedildi");
      setEditing(null);
      await load();
      // 1-click workflow: Create → auto stock_code → open label print dialog
      if (mode === "create" && opts.printAfter && saved?.id) {
        setLabelFor(saved);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt başarısız"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`'${it.name}' silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await usmApi.delete(`/inventory/${it.id}`);
      toast.success("Silindi");
      await load();
    } catch (e) { toast.error("Silme başarısız"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // Phase 47 #1 — Floating horizontal scrollbar refs (used by <FloatingScrollbar />).
  const tableScrollRef = React.useRef(null);
  const tableInnerRef = React.useRef(null);

  return (
    <div data-testid="usm-inventory">
      <PageHeader
        title={scopeTitle}
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: scopeTitle }]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Can perm="barcodes.print">
              <button
                data-testid="inventory-batch-print-btn"
                onClick={() => setBatchOpen(true)}
                disabled={selectedIds.size === 0}
                className="text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Seçilenler için toplu etiket yazdır"
              >
                <Printer size={14} /> Toplu Yazdır {selectedIds.size > 0 && <span className="font-mono text-[#C8A96A]">({selectedIds.size})</span>}
              </button>
            </Can>
            {/* Phase 53.8 — Bulk price update. Enabled only when at least
                one item is selected. Available on Silver / Gold / Watch. */}
            <Can perm="inventory.edit">
              <button
                data-testid="inventory-bulk-price-btn"
                onClick={() => setBulkPriceOpen(true)}
                disabled={selectedIds.size === 0}
                className="text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Seçili ürünlerin fiyatlarını toplu güncelle"
              >
                <DollarSign size={14} /> Toplu Fiyat Güncelle {selectedIds.size > 0 && <span className="font-mono text-[#C8A96A]">({selectedIds.size})</span>}
              </button>
            </Can>
            <Can perm="inventory.create">
              <Link data-testid="inventory-import-btn" to="/usm/inventory/import"
                className="text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
                <FileSpreadsheet size={14} /> CSV İçe Aktar
              </Link>
            </Can>
            <Can perm="inventory.create">
              <button data-testid="inventory-new-btn" onClick={() => setEditing({ mode: "create", data: EMPTY })}
                className="bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 inline-flex items-center gap-2">
                <Plus size={14} /> Yeni Ürün
              </button>
            </Can>
          </div>
        }
      />

      <InventorySummary
        branchId={filter.branch_id || ""}
        branchLabel={filter.branch_id ? (branches.find((b) => b.id === filter.branch_id)?.name || null) : null}
        refreshKey={summaryVersion}
        scope={scope}
      />

      {/* Phase 54.22 — Gold Inventory sub-category "sidebar" filter.
          Rendered as a horizontal chip strip immediately under the
          Summary so it reads as a category selector at the top of the
          Gold Inventory workspace. Never shown outside Gold scope so
          the existing Silver / Watch / All Inventory views are 100 %
          untouched. Clicking "Tümü" clears the filter (all gold
          products regardless of category). */}
      {scope === "gold" && (
        <div
          data-testid="gold-category-sidebar"
          className="bg-white border border-[#E7E5E4] mb-4 px-3 py-2 flex flex-wrap items-center gap-2"
        >
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] pr-2">Kategori</span>
          <button
            data-testid="gold-cat-all"
            onClick={() => setFilter((p) => ({ ...p, gold_category: "" }))}
            className={
              "text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors " +
              (filter.gold_category === ""
                ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                : "border-[#E7E5E4] hover:bg-[#FAFAF9]")
            }
          >
            Tümü
          </button>
          {GOLD_CATEGORIES.map((c) => (
            <button
              key={c.value}
              data-testid={`gold-cat-${c.value.toLowerCase()}`}
              onClick={() => setFilter((p) => ({ ...p, gold_category: c.value }))}
              className={
                "text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors " +
                (filter.gold_category === c.value
                  ? "border-[#C8A96A] bg-[#C8A96A] text-white"
                  : "border-[#E7E5E4] hover:bg-[#FAFAF9]")
              }
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#E7E5E4] mb-4 p-3 flex flex-wrap gap-3">
        <input data-testid="inventory-search" placeholder="Ara: stok kodu, barkod veya ürün adı..." value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          className="flex-1 min-w-[220px] px-3 py-2 border border-[#E7E5E4] text-sm" />
        {/* Phase 54.26 — Phone Scanner. Scanned code lands in the same
            debounced search input a USB scanner or keyboard would fill. */}
        <ScannerButton onScan={(code) => setQInput(String(code || "").trim())} />
        {isSuperAdmin && (
          <select
            data-testid="inventory-branch-filter"
            value={filter.branch_id}
            onChange={(e) => {
              // Phase 46.1 — toolbar dropdown updates prefs (single source of
              // truth). The sync effect will propagate to filter.branch_id
              // and the load effect will re-fetch. Empty ("Tüm şubeler")
              // clears the active branch preference too.
              setActiveBranch(e.target.value || null);
            }}
            className="px-3 py-2 border border-[#E7E5E4] text-sm"
          >
            <option value="">Tüm şubeler</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={filter.status} onChange={(e) => setFilter((p) => ({ ...p, status: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm">
          <option value="">Tüm durumlar</option>
          {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {/* Phase 53.8 + Phase 54.4 — Canonical category dropdown for
            Silver / Gold / Watch (Phase 54.4 reverted Watch back to
            canonical buckets; brand-as-category will return once real
            watch products are imported). Product counts stripped from
            labels for a cleaner luxury look. */}
        <select
          data-testid="inv-filter-category-slug"
          value={filter.category_slug}
          onChange={(e) => setFilter((p) => ({ ...p, category_slug: e.target.value, category_id: "" }))}
          className="px-3 py-2 border border-[#E7E5E4] text-sm"
        >
          <option value="">Tüm kategoriler</option>
          {CANONICAL_CATEGORIES_METAL.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
        {/* Phase 54.4 — Gold Inventory only: optional Brand filter using
            the dynamic usm_brands catalog (same list managed via
            Muhasebe → Markalar). Default "Tüm Markalar" means no
            filtering. Never required. */}
        {scope === "gold" && (
          <select
            data-testid="inv-filter-brand"
            value={filter.brand_id}
            onChange={(e) => setFilter((p) => ({ ...p, brand_id: e.target.value }))}
            className="px-3 py-2 border border-[#E7E5E4] text-sm"
          >
            <option value="">Tüm Markalar</option>
            {brandsForFilter.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
            <option value="__none__">Markasız</option>
          </select>
        )}
        {/* Phase 53.7 — Negative-stock quick toggle. Visible in every inventory
            workspace (Silver / Gold / Watch). Also mirrored inside the
            Advanced Filter panel for keyboard-friendly access. */}
        <button
          data-testid="inv-filter-negative"
          onClick={() => setFilter((p) => ({ ...p, negative_only: !p.negative_only }))}
          className={"px-3 py-2 border text-xs uppercase tracking-[0.15em] " + (filter.negative_only
            ? "border-red-500 bg-red-50 text-red-700"
            : "border-[#E7E5E4] hover:bg-[#FAFAF9] text-[#71717A]")}
          title="Sadece eksi stoklu ürünleri göster"
        >
          {filter.negative_only ? "Eksi Stok · Aktif" : "Eksi Stok"}
        </button>
        <button data-testid="inventory-refresh" onClick={() => load()} className="px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9]" title="Yenile"><RefreshCw size={14} /></button>
        <button data-testid="inv-adv-toggle" onClick={() => setShowAdv((v) => !v)} className={"px-3 py-2 border text-xs uppercase tracking-[0.15em] " + (showAdv ? "border-[#C8A96A] bg-[#C8A96A]/10" : "border-[#E7E5E4] hover:bg-[#FAFAF9]")}>Gelişmiş Filtre</button>
        <div className="relative">
          <button
            data-testid="inv-cols-toggle"
            onClick={() => setShowColsPicker((v) => !v)}
            className={"px-3 py-2 border text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1.5 " + (showColsPicker ? "border-[#C8A96A] bg-[#C8A96A]/10" : "border-[#E7E5E4] hover:bg-[#FAFAF9]")}
            title="Sütunları özelleştir"
          >
            <Columns3 size={14} /> Kolonlar
          </button>
          {showColsPicker && (
            <ColumnsPicker
              columns={effectiveColumns}
              onToggle={toggleColumn}
              onMove={moveColumn}
              onReset={resetColumns}
              onClose={() => setShowColsPicker(false)}
            />
          )}
        </div>
      </div>

      {/* Phase 46 — Feature #9 saved filter presets */}
      {(prefs?.inventory_filter_presets || []).length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Kayıtlı Filtreler:</span>
          {(prefs.inventory_filter_presets || []).map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1 border border-[#E7E5E4] bg-white text-xs">
              <button data-testid={`preset-apply-${p.id}`} onClick={() => applyPreset(p)} className="px-2.5 py-1 hover:bg-[#FAFAF9]">{p.name}</button>
              <button data-testid={`preset-delete-${p.id}`} onClick={() => deleteFilterPreset(p.id)} className="px-1.5 py-1 border-l border-[#E7E5E4] hover:bg-[#FAFAF9] text-[#71717A] hover:text-[#0A0A0A]" title="Sil">✕</button>
            </span>
          ))}
          <button data-testid="preset-save-btn" onClick={saveCurrentAsPreset} className="px-2.5 py-1 border border-dashed border-[#C8A96A] text-xs text-[#C8A96A] hover:bg-[#C8A96A]/5">+ Filtreyi kaydet</button>
        </div>
      )}

      {/* Phase 46 — Feature #7 advanced filter panel */}
      {showAdv && (
        <div className="bg-white border border-[#E7E5E4] mb-4 p-4 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Materyal</span>
            <select data-testid="inv-filter-material" value={filter.material} onChange={(e) => setFilter((p) => ({...p, material: e.target.value}))} className="px-2 py-1.5 border border-[#E7E5E4]">
              <option value="">Tümü</option>
              {materialOptionsForScope.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Ayar / Purity</span>
            <input value={filter.purity} onChange={(e) => setFilter((p) => ({...p, purity: e.target.value}))} placeholder="925, 750..." className="px-2 py-1.5 border border-[#E7E5E4]" />
          </label>
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Ağırlık (g)</span>
            <div className="flex gap-1 flex-wrap">
              {[[0,1],[1,2],[2,3],[3,5],[5,10]].map(([lo, hi]) => (
                <button key={lo} type="button" onClick={() => setFilter((p) => ({...p, min_weight: lo, max_weight: hi}))} className={"px-2 py-1 border text-[11px] " + ((+filter.min_weight===lo && +filter.max_weight===hi) ? "border-[#C8A96A] bg-[#C8A96A]/10 text-[#C8A96A]" : "border-[#E7E5E4] hover:bg-[#FAFAF9]")}>{lo}–{hi}g</button>
              ))}
              <input type="number" min="0" step="0.01" value={filter.min_weight} onChange={(e) => setFilter((p) => ({...p, min_weight: e.target.value}))} placeholder="min" className="w-20 px-2 py-1 border border-[#E7E5E4]" />
              <input type="number" min="0" step="0.01" value={filter.max_weight} onChange={(e) => setFilter((p) => ({...p, max_weight: e.target.value}))} placeholder="max" className="w-20 px-2 py-1 border border-[#E7E5E4]" />
            </div>
          </label>
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Min Fiyat (₺)</span>
            <input type="number" min="0" value={filter.min_price} onChange={(e) => setFilter((p) => ({...p, min_price: e.target.value}))} className="px-2 py-1.5 border border-[#E7E5E4]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Max Fiyat (₺)</span>
            <input type="number" min="0" value={filter.max_price} onChange={(e) => setFilter((p) => ({...p, max_price: e.target.value}))} className="px-2 py-1.5 border border-[#E7E5E4]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Stok Kodu</span>
            <input value={filter.stock_code} onChange={(e) => setFilter((p) => ({...p, stock_code: e.target.value}))} className="px-2 py-1.5 border border-[#E7E5E4]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">EAN-8</span>
            <input value={filter.ean8} onChange={(e) => setFilter((p) => ({...p, ean8: e.target.value}))} className="px-2 py-1.5 border border-[#E7E5E4]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Etiket Notu</span>
            <input value={filter.label_note} onChange={(e) => setFilter((p) => ({...p, label_note: e.target.value}))} className="px-2 py-1.5 border border-[#E7E5E4]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Fiyat Durumu</span>
            <select value={filter.has_price} onChange={(e) => setFilter((p) => ({...p, has_price: e.target.value}))} className="px-2 py-1.5 border border-[#E7E5E4]">
              <option value="">Tümü</option>
              <option value="true">Fiyatı olan</option>
              <option value="false">Fiyatı olmayan</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="uppercase tracking-[0.15em] text-[#71717A]">Görsel</span>
            <select value={filter.has_image} onChange={(e) => setFilter((p) => ({...p, has_image: e.target.value}))} className="px-2 py-1.5 border border-[#E7E5E4]">
              <option value="">Tümü</option>
              <option value="true">Görseli olan</option>
              <option value="false">Görseli olmayan</option>
            </select>
          </label>
          <div className="sm:col-span-3 lg:col-span-4 flex justify-end gap-2 pt-2 border-t border-[#F5F5F4]">
            <button onClick={() => setFilter({ q: "", branch_id: "", status: "", category_id: "", category_slug: "", brand_id: "", material: "", purity: "", min_weight: "", max_weight: "", min_price: "", max_price: "", stock_code: "", ean8: "", label_note: "", has_price: "", has_image: "", negative_only: false, gold_category: "" })} className="px-3 py-1.5 border border-[#E7E5E4] text-[11px] uppercase tracking-[0.15em] hover:bg-[#FAFAF9]">Temizle</button>
            <button data-testid="preset-save-inline" onClick={saveCurrentAsPreset} className="px-3 py-1.5 bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">Filtreyi Kaydet</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto" ref={tableScrollRef}>
        <table className="w-full text-sm min-w-[880px]" ref={tableInnerRef}>
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="w-10 px-3 py-3">
                <input
                  type="checkbox"
                  data-testid="inv-select-all"
                  className="accent-[#C8A96A] cursor-pointer"
                  checked={items.length > 0 && items.every((it) => selectedIds.has(it.id))}
                  ref={(el) => { if (el) el.indeterminate = items.some((it) => selectedIds.has(it.id)) && !items.every((it) => selectedIds.has(it.id)); }}
                  onChange={(e) => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) items.forEach((it) => next.add(it.id));
                      else items.forEach((it) => next.delete(it.id));
                      return next;
                    });
                  }}
                />
              </th>
              {visibleColumnsForRender.map((c) => {
                const meta = COLUMN_META[c.key];
                const alignCls = meta.align === "right" ? "text-right" : "text-left";
                return (
                  <th
                    key={c.key}
                    data-testid={`inv-th-${c.key}`}
                    style={{ width: c.width, minWidth: c.width }}
                    className={`${alignCls} px-3 py-3 relative select-none`}
                  >
                    {meta.label}
                    <ColumnResizeHandle initialWidth={c.width} onChange={(w) => setColumnWidth(c.key, w)} onCommit={commitColumnWidths} />
                  </th>
                );
              })}
              {/* Phase 48 #5 — sticky-right actions column: always accessible, no h-scroll needed. */}
              <th className="text-right px-2 py-3 w-[220px] sticky right-0 bg-[#FAFAF9] z-10 shadow-[-6px_0_10px_-6px_rgba(0,0,0,0.08)]">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              // Phase 54.24 — skeleton rows: 8 lightweight placeholder
              // rows so the table layout stays stable and the operator
              // sees the shape of the data instead of a "Yükleniyor..."
              // stall. Rows share width with the real ones so no CLS.
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={`sk-${i}`} data-testid="inv-row-skeleton" className="border-b border-[#F5F5F4]">
                  <td className="px-3 py-3"><div className="h-4 w-4 bg-[#F5F5F4] rounded-sm animate-pulse" /></td>
                  {visibleColumnsForRender.map((c) => (
                    <td key={c.key} className="px-3 py-3">
                      <div className="h-4 bg-[#F5F5F4] rounded-sm animate-pulse" style={{ width: `${40 + ((i * 7 + c.key.length * 3) % 45)}%` }} />
                    </td>
                  ))}
                  <td className="px-3 py-3"><div className="h-4 w-16 bg-[#F5F5F4] rounded-sm animate-pulse ml-auto" /></td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr><td colSpan={2 + visibleColumnsForRender.length} className="px-4 py-10 text-center text-[#71717A]">Henüz ürün yok.</td></tr>
            ) : items.map((it) => {
              const status = STATUS_LABEL[it.status] || { label: it.status, cls: "" };
              const showLegacy = it.barcode && it.barcode !== it.stock_code;
              const isSelected = selectedIds.has(it.id);
              const renderCell = (key) => {
                switch (key) {
                  case "photo":
                    // Phase 50 #1 + #7 — object-contain so full item is visible,
                    // click to open lightbox preview.
                    return it.photo_urls?.[0]
                      ? <img
                          src={it.photo_urls[0]}
                          alt={it.name}
                          loading="lazy"
                          decoding="async"
                          onClick={(e) => { e.stopPropagation(); setLightbox({ url: it.photo_urls[0], name: it.name }); }}
                          className="h-12 w-12 object-contain bg-[#FAFAF9] cursor-zoom-in hover:ring-2 hover:ring-[#C8A96A] transition"
                          data-testid={`inv-photo-${it.stock_code}`}
                        />
                      : <div className="h-12 w-12 bg-[#FAFAF9] border border-[#F5F5F4]" />;
                  case "stock_code":
                    return <span className="font-mono text-[12px] font-semibold tracking-wider">{it.stock_code || "—"}</span>;
                  case "barcode":
                    return <span className="font-mono text-[12px] text-[#71717A]">{showLegacy ? it.barcode : <span className="text-[#A8A29E]">—</span>}</span>;
                  case "name":
                    return (
                      <>
                        <div className="font-medium">{it.name}</div>
                        {it.sku && <div className="text-[11px] text-[#71717A]">SKU: {it.sku}</div>}
                      </>
                    );
                  case "branch": {
                    const sc = it.stock_code || it.barcode;
                    const breakdown = branchBreakdown.get(sc) || [];
                    const others = breakdown.filter((b) => b.branch_id !== it.branch_id);
                    const totalOther = others.reduce((a, b) => a + b.quantity, 0);
                    const title = others.map((b) => `${b.branch_name}: ${b.quantity}`).join("\n");
                    return (
                      <span className="text-[#52525B]">
                        {branchById[it.branch_id]?.name || "—"}
                        {others.length > 0 && (
                          <div
                            data-testid={`inv-multibranch-${it.stock_code}`}
                            title={title}
                            className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-[#C8A96A] cursor-help"
                          >
                            + {others.length} şube · {totalOther} adet
                          </div>
                        )}
                      </span>
                    );
                  }
                  case "material":
                    return <span className="text-[#52525B]">{it.material || "—"}{it.weight_grams != null ? ` · ${it.weight_grams}g` : ""}</span>;
                  case "ring_size": {
                    // Phase 54.25 #4 — read directly from it.ring_size, which
                    // the backend already only persists for ring-category
                    // products (server clears it otherwise via Phase 46).
                    const rs = it.ring_size;
                    return rs != null && rs !== "" && rs !== 0
                      ? <span className="font-mono text-[13px] text-[#0A0A0A]" data-testid={`inv-ringsize-${it.stock_code}`}>{rs}</span>
                      : <span className="text-[#A1A1AA]">—</span>;
                  }
                  case "price":
                    // Phase 50 #5 — Show purchase price prominently in Inventory
                    // (multi-currency selling prices already live in Price Lookup).
                    // The main row shows Alış (TRY); the small subline shows the
                    // sale TRY for cross-reference. Nothing about the backend
                    // schema changes — we just choose different fields to render.
                    return (
                      <div className="font-finance min-w-0">
                        <div className="text-[#0A0A0A] leading-tight truncate" data-testid={`inv-purchase-try-${it.stock_code}`}>
                          {it.purchase_price_try != null ? FMT.TRY.format(it.purchase_price_try) : "—"}
                        </div>
                        <div className="text-[10px] text-[#71717A] mt-0.5 leading-tight truncate">
                          <span className="uppercase tracking-[0.1em] mr-1">Satış:</span>
                          {it.prices?.TRY != null ? FMT.TRY.format(it.prices.TRY) : "—"}
                        </div>
                      </div>
                    );
                  case "quantity": {
                    // Phase 53.7 — Controlled oversell. Negative stock is
                    // rendered in bold red so staff can spot pieces needing
                    // correction at a glance.
                    const q = Number(it.quantity ?? 1);
                    const cls = q < 0
                      ? "font-finance text-[15px] text-red-600 font-bold"
                      : "font-finance text-[15px]";
                    return <span className={cls} data-testid={`inv-qty-${it.stock_code}`}>{it.quantity ?? 1}</span>;
                  }
                  case "min_quantity":
                    return <span className="font-mono text-[11px] text-[#71717A]" data-testid={`inv-minqty-${it.stock_code}`}>{it.min_quantity ?? 0}</span>;
                  case "stock_state": {
                    const ss = it.stock_state;
                    const cls = ss === "out_of_stock"
                      ? "bg-red-50 text-red-700 ring-red-200"
                      : ss === "low_stock"
                        ? "bg-amber-50 text-amber-700 ring-amber-200"
                        : "bg-emerald-50 text-emerald-700 ring-emerald-200";
                    const label = ss === "out_of_stock" ? "Stokta Yok" : ss === "low_stock" ? "Az Stok" : "Mevcut";
                    return (
                      <span data-testid={`inv-stock-state-${it.stock_code}`} className={"inline-block px-2 py-0.5 text-[10.5px] uppercase tracking-[0.15em] ring-1 " + cls}>
                        {label}
                      </span>
                    );
                  }
                  case "status":
                    return <span className={"text-[11px] uppercase tracking-[0.15em] " + status.cls}>● {status.label}</span>;
                  default:
                    return null;
                }
              };
              return (
                <tr key={it.id} data-testid={`inv-row-${it.stock_code || it.barcode}`} className={"border-t border-[#F5F5F4] " + (isSelected ? "bg-[#FFFBEF]" : "")}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      data-testid={`inv-select-${it.stock_code}`}
                      className="accent-[#C8A96A] cursor-pointer"
                      checked={isSelected}
                      onChange={(e) => {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(it.id);
                          else next.delete(it.id);
                          return next;
                        });
                      }}
                    />
                  </td>
                  {visibleColumnsForRender.map((c) => {
                    const meta = COLUMN_META[c.key];
                    const alignCls = meta.align === "right" ? "text-right" : "";
                    return (
                      <td key={c.key} style={{ width: c.width, minWidth: c.width }} className={`px-3 py-3 ${alignCls}`}>
                        {renderCell(c.key)}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right sticky right-0 z-10 shadow-[-6px_0_10px_-6px_rgba(0,0,0,0.08)]" style={{ backgroundColor: isSelected ? "#FFFBEF" : "white" }}>
                    {/* Phase 47 #2 / Phase 48 #5-6 — compact 40×40 touch targets
                        packed side-by-side so the 5 icons always fit on iPad
                        without horizontal scrolling. Column is sticky to the
                        right so the icons are always visible regardless of
                        horizontal scroll position. */}
                    <div className="inline-flex items-center gap-0.5">
                      <Can perm="barcodes.print">
                        <button onClick={() => setLabelFor(it)} className="inline-flex items-center justify-center w-10 h-10 hover:bg-[#F5F5F4] rounded transition-colors" title="Etiket Yazdır" data-testid={`inv-label-${it.stock_code}`}>
                          <Printer size={17} />
                        </button>
                      </Can>
                      <Link to={`/usm/inventory/${it.id}/timeline`} className="inline-flex items-center justify-center w-10 h-10 hover:bg-[#F5F5F4] rounded transition-colors" title="Zaman Çizelgesi" data-testid={`inv-timeline-${it.stock_code}`}><History size={17} /></Link>
                      <Can perm="barcodes.print">
                        <button onClick={() => downloadBarcode(it)} className="inline-flex items-center justify-center w-10 h-10 hover:bg-[#F5F5F4] rounded transition-colors" title="Barkodu indir" data-testid={`inv-barcode-${it.stock_code}`}><Barcode size={17} /></button>
                      </Can>
                      <Can perm="inventory.edit">
                        <button onClick={() => setEditing({ mode: "edit", data: { ...EMPTY, ...it } })} className="inline-flex items-center justify-center w-10 h-10 hover:bg-[#F5F5F4] rounded transition-colors" title="Düzenle"><Pencil size={17} /></button>
                      </Can>
                      <Can perm="inventory.delete">
                        <button onClick={() => remove(it)} className="inline-flex items-center justify-center w-10 h-10 hover:bg-red-50 rounded text-red-600 transition-colors" title="Sil"><Trash2 size={17} /></button>
                      </Can>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Phase 47 #1 — Floating horizontal scrollbar. Mirrors the table's
          horizontal scroll position and stays pinned to the bottom of the
          viewport while the user scrolls vertically. Only rendered when the
          table actually overflows horizontally. */}
      <FloatingScrollbar scrollRef={tableScrollRef} innerRef={tableInnerRef} />

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-[#71717A]">
          <span>Toplam {total} ürün</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => { const np = page - 1; setPage(np); load(np); }} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-30">‹</button>
            <span>Sayfa {page}/{totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => { const np = page + 1; setPage(np); load(np); }} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-30">›</button>
          </div>
        </div>
      )}

      {editing && (
        <InventoryEditor
          mode={editing.mode}
          data={editing.data}
          branches={branches}
          categories={categories}
          categoryById={categoryById}
          canCost={canCost}
          allowedMaterials={materialOptionsForScope}
          scope={scope}
          onClose={() => setEditing(null)}
          onSave={(d, opts) => onSave(d, editing.mode, editing.data?.id, opts)}
        />
      )}
      {labelFor && <LabelPrintDialog item={labelFor} category={categoryById[labelFor.category_id]} onClose={() => setLabelFor(null)} />}
      {batchOpen && (
        <BatchLabelPrintDialog
          items={items.filter((it) => selectedIds.has(it.id))}
          onClose={() => setBatchOpen(false)}
        />
      )}
      {bulkPriceOpen && (
        <BulkPriceDialog
          items={items.filter((it) => selectedIds.has(it.id))}
          onClose={() => setBulkPriceOpen(false)}
          onDone={() => { setBulkPriceOpen(false); setSelectedIds(new Set()); load(); }}
        />
      )}
      {/* Phase 50 #7 — Quick image preview lightbox. Click outside or press ESC
          to close. Displays the full uncropped photo so operators can identify
          an item without opening the editor. */}
      {lightbox && <ImageLightbox url={lightbox.url} caption={lightbox.name} onClose={() => setLightbox(null)} />}
    </div>
  );
}

// ─────────────────────────────  Bulk Price Dialog (Phase 53.8 + 53.9)  ─────────

/**
 * Bulk-updates sale_price_try and/or purchase_price_try across every
 * selected inventory item. Supports TWO modes:
 *   1. Fixed  — the operator types a fixed TRY value (Phase 53.8 default).
 *   2. Formula — the operator picks an operation (+% / -% / +₺ / -₺ / ×)
 *      and a value; the engine computes the new price per item. Phase 53.9.
 *
 * Optional rounding step (none / 10 / 50 / 100) rounds the computed price
 * to the nearest bucket. A live 3-row preview shows before-and-after
 * values so operators can sanity-check the formula before firing the
 * batch. Requests go through POST /inventory/bulk-price which writes one
 * rich audit row per item (old, new, formula, rounding).
 */

const FORMULA_OPS = [
  { op: "pct_up",   label: "% Artır",    suffix: "%" },
  { op: "pct_down", label: "% Azalt",    suffix: "%" },
  { op: "amt_up",   label: "+ Tutar",    suffix: "₺" },
  { op: "amt_down", label: "− Tutar",    suffix: "₺" },
  { op: "multiply", label: "× Çarp",     suffix: "" },
];

const ROUND_STEPS = [
  { key: "none", label: "Yuvarlama Yok",  step: null },
  { key: "10",   label: "En Yakın 10 ₺",  step: 10 },
  { key: "50",   label: "En Yakın 50 ₺",  step: 50 },
  { key: "100",  label: "En Yakın 100 ₺", step: 100 },
];

function applyFormulaClient(price, op, value) {
  if (price == null) return null;
  const p = Number(price);
  const v = Number(value);
  switch (op) {
    case "pct_up":   return p * (1 + v / 100);
    case "pct_down": return p * (1 - v / 100);
    case "amt_up":   return p + v;
    case "amt_down": return p - v;
    case "multiply": return p * v;
    default: return p;
  }
}

function roundPriceClient(v, step) {
  if (v == null) return null;
  if (!step) return Math.round(v * 100) / 100;
  return Math.round(v / step) * step;
}

function BulkPriceDialog({ items, onClose, onDone }) {
  const [mode, setMode] = useState("fixed");           // "fixed" | "formula"
  const [target, setTarget] = useState("sale");        // "sale" | "purchase" | "both"
  // Fixed mode
  const [salePrice, setSalePrice] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  // Formula mode
  const [formulaOp, setFormulaOp] = useState("pct_up");
  const [formulaValue, setFormulaValue] = useState("");
  const [rounding, setRounding] = useState("none");
  // Flow state
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);

  const count = items.length;
  const roundStep = ROUND_STEPS.find((r) => r.key === rounding)?.step || null;

  const fmt = (v) => v == null ? "—" : Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";

  // Compute new price for a single item + field. Mirrors the backend
  // exactly so the operator's preview is faithful.
  const computeNew = (oldPrice, field) => {
    if (target !== field && target !== "both") return oldPrice;
    if (mode === "fixed") {
      const raw = field === "sale" ? salePrice : purchasePrice;
      if (raw === "" || isNaN(Number(raw))) return oldPrice;
      return roundPriceClient(Number(raw), roundStep);
    }
    if (formulaValue === "" || isNaN(Number(formulaValue))) return oldPrice;
    return roundPriceClient(applyFormulaClient(oldPrice, formulaOp, formulaValue), roundStep);
  };

  const preview = items.slice(0, 3).map((it) => ({
    stock_code: it.stock_code,
    name: it.name,
    old_sale: it.sale_price_try,
    new_sale: computeNew(it.sale_price_try, "sale"),
    old_purchase: it.purchase_price_try,
    new_purchase: computeNew(it.purchase_price_try, "purchase"),
  }));

  const inputsValid = (() => {
    if (mode === "fixed") {
      if (target === "sale") return salePrice !== "" && !isNaN(Number(salePrice)) && Number(salePrice) >= 0;
      if (target === "purchase") return purchasePrice !== "" && !isNaN(Number(purchasePrice)) && Number(purchasePrice) >= 0;
      return (salePrice !== "" && !isNaN(Number(salePrice)) && Number(salePrice) >= 0) &&
             (purchasePrice !== "" && !isNaN(Number(purchasePrice)) && Number(purchasePrice) >= 0);
    }
    // formula
    if (formulaValue === "" || isNaN(Number(formulaValue))) return false;
    const v = Number(formulaValue);
    if (formulaOp === "multiply" && v <= 0) return false;
    if (v < 0) return false;
    return true;
  })();

  const runUpdate = async () => {
    setSaving(true); setProgress(0);
    const body = {
      item_ids: items.map((i) => i.id),
      target,
      mode,
      rounding,
    };
    if (mode === "fixed") {
      if (target === "sale" || target === "both") body.sale_price_try = Number(salePrice);
      if (target === "purchase" || target === "both") body.purchase_price_try = Number(purchasePrice);
    } else {
      body.formula = { op: formulaOp, value: Number(formulaValue) };
    }
    try {
      const r = await usmApi.post("/inventory/bulk-price", body);
      const d = r.data || {};
      setProgress(d.updated || 0);
      if ((d.failed?.length || 0) === 0) {
        toast.success(`${d.updated || 0} ürün başarıyla güncellendi.`);
      } else {
        toast.error(`${d.updated || 0} başarılı · ${d.failed.length} başarısız (negatif fiyat vb.)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu güncelleme başarısız");
    } finally {
      setSaving(false);
      onDone();
    }
  };

  const activeOp = FORMULA_OPS.find((o) => o.op === formulaOp);
  const previewLabel = mode === "fixed"
    ? (target === "both" ? "Sabit değer (Satış + Alış)" : (target === "sale" ? "Sabit değer (Satış)" : "Sabit değer (Alış)"))
    : `${activeOp?.label} ${formulaValue || "…"}${activeOp?.suffix || ""}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div data-testid="bulk-price-dialog" className="bg-white w-full max-w-3xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg">Toplu Fiyat Güncelle</h2>
            <p className="text-[11px] text-[#71717A] mt-0.5">{count} ürün seçildi</p>
          </div>
          <button onClick={onClose} className="text-[#71717A] hover:text-[#0A0A0A]"><XIcon size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Mode toggle */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.15em] text-[#71717A] mb-1.5">Güncelleme Modu</label>
            <div className="inline-flex border border-[#E7E5E4] p-0.5">
              <button data-testid="bulk-price-mode-fixed"
                onClick={() => setMode("fixed")}
                className={"px-4 py-1.5 text-xs uppercase tracking-[0.15em] " + (mode === "fixed" ? "bg-[#0A0A0A] text-white" : "hover:bg-[#FAFAF9]")}
              >Sabit Değer</button>
              <button data-testid="bulk-price-mode-formula"
                onClick={() => setMode("formula")}
                className={"px-4 py-1.5 text-xs uppercase tracking-[0.15em] " + (mode === "formula" ? "bg-[#0A0A0A] text-white" : "hover:bg-[#FAFAF9]")}
              >Formül</button>
            </div>
          </div>

          {/* Target */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.15em] text-[#71717A] mb-1.5">Uygulanacak Fiyat</label>
            <div className="inline-flex border border-[#E7E5E4] p-0.5">
              {[
                { key: "sale",     label: "Satış" },
                { key: "purchase", label: "Alış" },
                { key: "both",     label: "Her İkisi" },
              ].map((t) => (
                <button key={t.key} data-testid={`bulk-price-target-${t.key}`}
                  onClick={() => setTarget(t.key)}
                  className={"px-4 py-1.5 text-xs uppercase tracking-[0.15em] " + (target === t.key ? "bg-[#C8A96A] text-white" : "hover:bg-[#FAFAF9]")}
                >{t.label}</button>
              ))}
            </div>
          </div>

          {/* Value inputs */}
          {mode === "fixed" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(target === "sale" || target === "both") && (
                <div>
                  <label className="block text-[10.5px] uppercase tracking-[0.15em] text-[#71717A] mb-1">Yeni Satış Fiyatı (₺)</label>
                  <input data-testid="bulk-price-sale-input" type="number" min="0" step="0.01"
                    value={salePrice} onChange={(e) => setSalePrice(e.target.value)}
                    className="w-full px-3 py-2 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm font-finance" />
                </div>
              )}
              {(target === "purchase" || target === "both") && (
                <div>
                  <label className="block text-[10.5px] uppercase tracking-[0.15em] text-[#71717A] mb-1">Yeni Alış Fiyatı (₺)</label>
                  <input data-testid="bulk-price-purchase-input" type="number" min="0" step="0.01"
                    value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)}
                    className="w-full px-3 py-2 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm font-finance" />
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-3">
              <div>
                <label className="block text-[10.5px] uppercase tracking-[0.15em] text-[#71717A] mb-1">İşlem</label>
                <select data-testid="bulk-price-formula-op" value={formulaOp} onChange={(e) => setFormulaOp(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E7E5E4] text-sm bg-white">
                  {FORMULA_OPS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10.5px] uppercase tracking-[0.15em] text-[#71717A] mb-1">Değer {activeOp?.suffix && <span className="text-[#A8A29E]">({activeOp.suffix})</span>}</label>
                <input data-testid="bulk-price-formula-value" type="number" min="0" step="0.01"
                  value={formulaValue} onChange={(e) => setFormulaValue(e.target.value)}
                  placeholder={formulaOp === "multiply" ? "1.05" : formulaOp.startsWith("pct") ? "10" : "250"}
                  className="w-full px-3 py-2 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm font-finance" />
              </div>
            </div>
          )}

          {/* Rounding */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.15em] text-[#71717A] mb-1">Yuvarlama</label>
            <select data-testid="bulk-price-rounding" value={rounding} onChange={(e) => setRounding(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-[#E7E5E4] text-sm bg-white">
              {ROUND_STEPS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>

          {/* Live Preview */}
          {inputsValid && (
            <div data-testid="bulk-price-preview" className="border border-[#E7E5E4] bg-[#FAFAF9]">
              <div className="px-3 py-2 border-b border-[#E7E5E4] flex items-center justify-between">
                <p className="text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">Önizleme — İlk 3 Ürün</p>
                <span className="text-[10.5px] font-mono text-[#C8A96A]">{previewLabel}</span>
              </div>
              <div className="divide-y divide-[#E7E5E4]">
                {preview.map((p) => (
                  <div key={p.stock_code} data-testid={`bulk-price-preview-row-${p.stock_code}`} className="px-3 py-2 grid grid-cols-[minmax(0,1fr)_auto] gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="font-mono text-[11px] text-[#71717A]">{p.stock_code}</div>
                      <div className="truncate">{p.name}</div>
                    </div>
                    <div className="text-right text-[12.5px] space-y-0.5">
                      {(target === "sale" || target === "both") && (
                        <div>
                          <span className="text-[#A8A29E]">Satış:</span>{" "}
                          <span className="line-through text-[#A8A29E] font-finance">{fmt(p.old_sale)}</span>{" "}
                          <span className="font-finance text-[#0A0A0A] font-medium">{fmt(p.new_sale)}</span>
                        </div>
                      )}
                      {(target === "purchase" || target === "both") && (
                        <div>
                          <span className="text-[#A8A29E]">Alış:</span>{" "}
                          <span className="line-through text-[#A8A29E] font-finance">{fmt(p.old_purchase)}</span>{" "}
                          <span className="font-finance text-[#0A0A0A] font-medium">{fmt(p.new_purchase)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {saving && (
            <div>
              <div className="flex justify-between text-[11px] text-[#71717A] mb-1">
                <span>Uygulanıyor…</span>
                <span data-testid="bulk-price-progress">{progress} / {count}</span>
              </div>
              <div className="h-1.5 bg-[#F5F5F4] overflow-hidden">
                <div className="h-full bg-[#C8A96A] transition-all" style={{ width: `${(progress / Math.max(1, count)) * 100}%` }} />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E7E5E4] flex justify-end gap-3 flex-wrap">
          <button onClick={onClose} disabled={saving}
            className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9] disabled:opacity-40">İptal</button>
          {confirming ? (
            <button data-testid="bulk-price-confirm" onClick={runUpdate} disabled={saving}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
              {saving ? "Güncelleniyor…" : `Onaylıyorum · ${count} ürünü güncelle`}
            </button>
          ) : (
            <button data-testid="bulk-price-apply" onClick={() => setConfirming(true)} disabled={!inputsValid || saving}
              className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">Devam Et</button>
          )}
        </div>

        {confirming && !saving && (
          <div data-testid="bulk-price-confirm-msg" className="px-6 py-3 bg-amber-50 border-t border-amber-200 text-[13px] text-amber-900">
            <b>{count} ürünün fiyatı güncellenecek.</b> İşlem geri alınamaz. Devam etmek istiyor musunuz?<br />
            <span className="text-[11px] text-amber-800 font-mono">{previewLabel}{rounding !== "none" ? ` · ${ROUND_STEPS.find((r) => r.key === rounding).label}` : ""}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────  Inventory Editor  ─────────────────────────────

function InventoryEditor({ mode, data, branches, categories, categoryById, canCost, allowedMaterials, scope, onClose, onSave }) {
  const { isSuperAdmin, employee, hasPerm } = useUsmAuth();
  const canPrint = hasPerm("barcodes.print");
  // Phase 53.3 — Load active brands once when the editor opens so the Gold
  // material's Brand dropdown is populated. Fully dynamic — read from
  // Accounting → Markalar. Historical brand_id kept on legacy rows even when
  // deactivated (the dropdown auto-prepends the current value if missing).
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    usmApi.get("/accounting/brands?status=active")
      .then((r) => setBrands(r.data || []))
      .catch(() => setBrands([]));
  }, []);
  // Phase 47 #4 / 47.2 #3 / 48 #7 — remember last selected branch, category, material, gender.
  // Phase 49 — also remember last silver purchase USD/g + pricing multiplier.
  const LAST_BRANCH_KEY = "usm_last_branch_id";
  const LAST_CATEGORY_KEY = "usm_last_category_id";
  const LAST_MATERIAL_KEY = "usm_last_material";
  const LAST_GENDER_KEY = "usm_last_gender";
  const LAST_SILVER_USD_KEY = "usm_last_silver_purchase_usd";
  const LAST_MULT_KEY = "usm_last_pricing_multiplier";
  const lastBranch = typeof window !== "undefined" ? sessionStorage.getItem(LAST_BRANCH_KEY) : "";
  const lastCategory = typeof window !== "undefined" ? sessionStorage.getItem(LAST_CATEGORY_KEY) : "";
  const lastMaterial = typeof window !== "undefined" ? sessionStorage.getItem(LAST_MATERIAL_KEY) : "";
  const lastGender = typeof window !== "undefined" ? sessionStorage.getItem(LAST_GENDER_KEY) : "";
  const lastSilverUsd = typeof window !== "undefined" ? sessionStorage.getItem(LAST_SILVER_USD_KEY) : "";
  const lastMultiplier = typeof window !== "undefined" ? sessionStorage.getItem(LAST_MULT_KEY) : "";
  // Phase 54.25 #2 — Remember last brand + gold category for gold inventory.
  const lastGoldBrandId = (typeof window !== "undefined" && scope === "gold") ? sessionStorage.getItem("usm_last_gold_brand_id") || "" : "";
  const lastGoldCategory = (typeof window !== "undefined" && scope === "gold") ? sessionStorage.getItem("usm_last_gold_category") || "" : "";
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...data,
    branch_id: data?.branch_id
      || (mode === "create" && lastBranch ? lastBranch : "")
      || (isSuperAdmin ? "" : employee?.branch_id || ""),
    category_id: data?.category_id || (mode === "create" && lastCategory ? lastCategory : ""),
    material: data?.material || (mode === "create" && lastMaterial ? lastMaterial : ""),
    gender: data?.gender || (mode === "create" && lastGender ? lastGender : ""),
    // Phase 54.25 #2 — sticky brand + gold_category on create.
    brand_id: data?.brand_id ?? (mode === "create" ? lastGoldBrandId : ""),
    gold_category: data?.gold_category ?? (mode === "create" ? lastGoldCategory : ""),
    weight_grams: data?.weight_grams ?? "",
    purchase_price_try: data?.purchase_price_try ?? "",
    sale_price_try: data?.sale_price_try ?? "",
    ring_size: data?.ring_size ?? "",
    label_note: data?.label_note ?? "",
  }));

  // ─── Phase 49 — Silver Pricing Engine + Phase 54.22 — Gold Pricing Engine
  //
  // Auto-pricing runs whenever the current material has a registered pricing
  // engine (getEngineForMaterial). Silver computes purchase/sale in TRY
  // from weight × USD rate × USD-per-gram × multiplier; Gold uses
  // TCMB Gold/TRY (÷1000 applied server-side) × buy/sell multiplier × weight.
  //
  // Locks: purchaseLocked / saleLocked are UI-local; when true, the auto
  // recomputation SKIPS that field so the operator's typed value survives.
  // Clicking the reset (↺) icon un-locks and immediately recomputes.
  const engine = getEngineForMaterial(form.material);
  const isGoldEngine = engine?.id === "gold";
  const [silverPurchaseUsd, setSilverPurchaseUsd] = useState(() => (
    // Prefer any value already persisted on the item, then session, else ""
    data?.silver_purchase_usd ?? (mode === "create" && lastSilverUsd ? lastSilverUsd : "")
  ));
  const [pricingMultiplier, setPricingMultiplier] = useState(() => (
    data?.pricing_multiplier
    ?? (mode === "create" && lastMultiplier ? Number(lastMultiplier) : (engine?.defaultMultiplier ?? 3))
  ));
  // Phase 54.22 — Gold buy + sell multipliers. Independent per-piece
  // markups against the TCMB Gold/TRY rate (both applied directly to
  // weight × rate). Persisted on the item so re-opening the editor
  // rehydrates the exact form the operator left.
  const LAST_GOLD_BUY_KEY = "usm_last_gold_buy_multiplier";
  const LAST_GOLD_SELL_KEY = "usm_last_gold_sell_multiplier";
  const lastGoldBuy = typeof window !== "undefined" ? sessionStorage.getItem(LAST_GOLD_BUY_KEY) : "";
  const lastGoldSell = typeof window !== "undefined" ? sessionStorage.getItem(LAST_GOLD_SELL_KEY) : "";
  const [goldBuyMultiplier, setGoldBuyMultiplier] = useState(() => (
    data?.gold_buy_multiplier
      ?? (mode === "create" && lastGoldBuy ? Number(lastGoldBuy) : (engine?.defaultBuyMultiplier ?? 700))
  ));
  const [goldSellMultiplier, setGoldSellMultiplier] = useState(() => (
    data?.gold_sell_multiplier
      ?? (mode === "create" && lastGoldSell ? Number(lastGoldSell) : (engine?.defaultSellMultiplier ?? 800))
  ));
  // On edit (mode!=="create"), start LOCKED so we never silently overwrite an
  // existing hand-set price. On create, unlocked so typing weight+USD auto-fills.
  const [purchaseLocked, setPurchaseLocked] = useState(mode !== "create");
  const [saleLocked, setSaleLocked] = useState(mode !== "create");

  // TCMB rate — readonly. Silver reads USD from /api/rates; Gold reads
  // the TCMB Gold/TRY rate from /api/usm/pricing/gold-rate (backend has
  // already divided by 1000, so the value returned here is per-gram).
  const [usdRate, setUsdRate] = useState(null);
  const [goldTryPerGram, setGoldTryPerGram] = useState(null);
  const [goldRateMeta, setGoldRateMeta] = useState(null); // {raw_rate, updated_at, updated_by}
  const fetchGoldRate = React.useCallback(async () => {
    try {
      const r = await usmApi.get("/pricing/gold-rate");
      const d = r.data || {};
      setGoldTryPerGram(d.gold_try_per_gram || null);
      setGoldRateMeta(d);
    } catch { /* offline — leave null; auto-calc guards on nulls */ }
  }, []);
  useEffect(() => {
    if (!engine) return; // no need to fetch if no engine active
    let alive = true;
    (async () => {
      if (isGoldEngine) {
        await fetchGoldRate();
      } else {
        try {
          const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
          const r = await fetch(`${base}/api/rates`, { headers: { "Accept": "application/json" } });
          if (!alive || !r.ok) return;
          const data = await r.json();
          const usd = (data.currencies || []).find((c) => c.code === "USD");
          const rate = usd?.forex_selling || usd?.display_sell || null;
          if (rate && alive) setUsdRate(rate);
        } catch { /* offline — leave usdRate null; auto-calc guards on nulls */ }
      }
    })();
    return () => { alive = false; };
  }, [engine, isGoldEngine, fetchGoldRate]);

  // Recompute purchase & sale whenever ANY input changes. Locks are respected.
  useEffect(() => {
    if (!engine) return;
    let purchaseTry, saleTry;
    if (isGoldEngine) {
      // Phase 54.22 — Gold: buy & sell multipliers act INDEPENDENTLY on
      // the raw rate × weight. Sell is NOT computed off Purchase.
      purchaseTry = engine.computePurchaseTry({
        weightGrams: form.weight_grams,
        goldTryPerGram,
        buyMultiplier: goldBuyMultiplier,
      });
      saleTry = engine.computeSaleTry({
        weightGrams: form.weight_grams,
        goldTryPerGram,
        sellMultiplier: goldSellMultiplier,
      });
    } else {
      purchaseTry = engine.computePurchaseTry({
        weightGrams: form.weight_grams,
        usdRateTry: usdRate,
        purchaseUsdPerGram: silverPurchaseUsd,
      });
      // For sale we use the FRESHLY computed purchase price when unlocked, but
      // fall back to whatever the form currently has when purchase is locked
      // (so a hand-set cost still drives the sale multiplier).
      const effectivePurchase = !purchaseLocked && purchaseTry != null ? purchaseTry : form.purchase_price_try;
      saleTry = engine.computeSaleTry(effectivePurchase, pricingMultiplier);
    }
    if (purchaseTry != null && !purchaseLocked) {
      setForm((prev) => prev.purchase_price_try === purchaseTry ? prev : { ...prev, purchase_price_try: purchaseTry });
    }
    if (saleTry != null && !saleLocked) {
      setForm((prev) => prev.sale_price_try === saleTry ? prev : { ...prev, sale_price_try: saleTry });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, isGoldEngine, form.weight_grams, usdRate, goldTryPerGram, silverPurchaseUsd, pricingMultiplier, goldBuyMultiplier, goldSellMultiplier, purchaseLocked, saleLocked, form.purchase_price_try]);

  const resetPurchase = () => setPurchaseLocked(false);
  const unlockPurchase = () => setPurchaseLocked(true);
  const resetSale = () => setSaleLocked(false);
  const unlockSale = () => setSaleLocked(true);

  // Attach engine inputs onto the payload so the outer save handler can
  // persist them onto the item AND remember them in sessionStorage for the
  // next silver / gold product.
  const mergePricingState = (payload) => ({
    ...payload,
    silver_purchase_usd: !isGoldEngine && engine && silverPurchaseUsd !== "" ? Number(silverPurchaseUsd) : payload.silver_purchase_usd,
    pricing_multiplier: !isGoldEngine && engine ? Number(pricingMultiplier) : payload.pricing_multiplier,
    // Phase 54.22 — persist gold buy/sell multipliers + sub-category.
    gold_buy_multiplier: isGoldEngine && goldBuyMultiplier !== "" ? Number(goldBuyMultiplier) : payload.gold_buy_multiplier,
    gold_sell_multiplier: isGoldEngine && goldSellMultiplier !== "" ? Number(goldSellMultiplier) : payload.gold_sell_multiplier,
  });
  // Phase 54.23.7 — per-file upload state (Map keyed by clientId).
  // Each entry: {file, previewUrl, progress:0-100, status:'uploading'|'error'}.
  // Replaces the previous single global `uploading` boolean so the UI
  // stays responsive during parallel uploads and each photo shows its
  // own progress bar.
  const [pendingUploads, setPendingUploads] = useState({});
  const pendingUploadsRef = React.useRef(pendingUploads);
  useEffect(() => { pendingUploadsRef.current = pendingUploads; }, [pendingUploads]);
  const pendingCount = Object.keys(pendingUploads).length;
  const uploading = pendingCount > 0;  // back-compat alias for the button/label copy

  // Revoke every blob: URL on unmount to prevent memory leaks (Phase 54.23.7 §5)
  useEffect(() => {
    return () => {
      Object.values(pendingUploadsRef.current || {}).forEach((p) => {
        try { if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl); } catch { /* revoke best-effort */ }
      });
    };
  }, []);

  const [previewCode, setPreviewCode] = useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Preview the next stock_code at create time (non-reserving).
  useEffect(() => {
    if (mode !== "create") return;
    let mounted = true;
    usmApi.get("/inventory/next-code?reserve=false").then((r) => {
      if (mounted) setPreviewCode(r.data.stock_code);
    }).catch(() => { /* ignore */ });
    return () => { mounted = false; };
  }, [mode]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    // Phase 46 — client-side validation with meaningful errors.
    const ACCEPT_EXT = ["jpg", "jpeg", "png", "webp", "heic", "heif", "gif", "bmp", "tiff", "tif"];
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    if (ext && !ACCEPT_EXT.includes(ext)) {
      toast.error(`Desteklenmeyen format: .${ext}. Kabul edilen: JPG, PNG, WEBP, HEIC`);
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error(`Dosya çok büyük (${(file.size/1024/1024).toFixed(1)} MB) — maks 25 MB`);
      return;
    }
    // Phase 54.23.7 — Instant local preview via URL.createObjectURL so
    // the operator sees the thumbnail immediately (no wait for the
    // server round trip). Progress is tracked per-file via axios
    // onUploadProgress. Uploads run in parallel — the backend event
    // loop no longer serialises them (see Phase 54.23.7 backend edits).
    const clientId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    let previewUrl = "";
    try { previewUrl = URL.createObjectURL(file); } catch { /* preview optional */ }
    setPendingUploads((p) => ({ ...p, [clientId]: { file, previewUrl, progress: 0, status: "uploading" } }));

    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "usm-inventory");
      const r = await usmApi.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
          setPendingUploads((p) => (p[clientId] ? { ...p, [clientId]: { ...p[clientId], progress: pct } } : p));
        },
      });
      const url = r.data.url;
      // Atomically clear the pending slot and append the server URL.
      // Uses the functional setter for both slices to avoid stale reads
      // when multiple uploads finish in the same tick.
      setForm((prev) => ({
        ...prev,
        photo_urls: [url, ...(prev.photo_urls || [])].slice(0, 5),
      }));
      setPendingUploads((p) => {
        const next = { ...p };
        const entry = next[clientId];
        if (entry?.previewUrl) {
          try { URL.revokeObjectURL(entry.previewUrl); } catch { /* revoke best-effort */ }
        }
        delete next[clientId];
        return next;
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Yükleme başarısız";
      toast.error(msg);
      // Drop the failed entry (release its blob URL) so the user can retry.
      setPendingUploads((p) => {
        const next = { ...p };
        const entry = next[clientId];
        if (entry?.previewUrl) {
          try { URL.revokeObjectURL(entry.previewUrl); } catch { /* revoke best-effort */ }
        }
        delete next[clientId];
        return next;
      });
    }
  };

  // Phase 54.23.7 — start every picked file's upload immediately in
  // parallel. Respects the 5-photo ceiling across (already-uploaded +
  // currently-uploading) so you never queue up phantom slots.
  const enqueueUploads = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const currentCount = (form.photo_urls?.length || 0) + Object.keys(pendingUploadsRef.current || {}).length;
    const room = Math.max(0, 5 - currentCount);
    if (room <= 0) {
      toast.error("En fazla 5 görsel yüklenebilir");
      return;
    }
    if (list.length > room) {
      toast.info(`Sadece ilk ${room} görsel yüklenecek (maks 5)`);
    }
    list.slice(0, room).forEach((f) => { uploadPhoto(f); });
  };

  const onPhotoDrop = (e) => {
    e.preventDefault();
    enqueueUploads(e.dataTransfer?.files);
  };

  const cat = form.category_id ? categoryById[form.category_id] : null;
  const isRing = isRingCategory(cat);

  const valid = useMemo(() => {
    if (!form.name) return false;
    if (!form.branch_id) return false;
    // Phase 46 — price is now optional. Only reject explicitly-negative values.
    if (form.sale_price_try !== "" && form.sale_price_try != null && Number(form.sale_price_try) < 0) return false;
    // Phase 54.22 — Gold Inventory sub-category is REQUIRED when the
    // material is gold. Silver / Watch / other materials unaffected.
    if (form.material && GOLD_MATERIALS_SET.has(form.material) && !form.gold_category) return false;
    return true;
  }, [form]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div data-testid="inventory-editor" className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h2 className="font-heading text-xl">{mode === "create" ? "Yeni Envanter Ürünü" : "Ürünü Düzenle"}</h2>
          <button onClick={onClose} className="text-[#71717A] text-lg leading-none">✕</button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Stock code preview (read-only on create, editable on edit by admin) */}
          {mode === "create" ? (
            <Field label="Stok Kodu (Otomatik)" className="sm:col-span-2">
              <div className="px-3 py-2.5 border border-[#E7E5E4] bg-[#FAFAF9] font-mono tracking-wider text-base">
                {previewCode || "UZG…"}
              </div>
              <p className="text-[10px] text-[#71717A] mt-1">Kaydedildiğinde sıra ile otomatik atanır.</p>
            </Field>
          ) : (
            <Field label="Stok Kodu" className="sm:col-span-2">
              <div className="px-3 py-2.5 border border-[#E7E5E4] bg-[#FAFAF9] font-mono tracking-wider">
                {form.stock_code || "—"}
              </div>
            </Field>
          )}

          <Field label="Legacy Barkod (opsiyonel)">
            {/* Phase 54.26.1 — Phone Scanner beside the legacy barcode input.
                Scanned codes are trimmed & set on `form.barcode` via the
                same setter as manual typing. */}
            <div className="flex items-center gap-2">
              <input data-testid="inv-barcode" value={form.barcode} onChange={(e) => set("barcode", e.target.value.trim())} className={inputCls + " flex-1 min-w-0"} placeholder="EAN-13 vb."  />
              <ScannerButton onScan={(code) => set("barcode", String(code || "").trim())} />
            </div>
          </Field>
          <Field label="SKU (opsiyonel)"><input value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} className={inputCls} /></Field>

          <Field label="Ürün Adı *" className="sm:col-span-2"><input data-testid="inv-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>

          <Field label="Şube *">
            <select data-testid="inv-branch" value={form.branch_id} onChange={(e) => set("branch_id", e.target.value)} disabled={!isSuperAdmin && mode === "edit"} className={inputCls}>
              <option value="">Seçin...</option>
              {branches.filter((b) => b.is_active).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Kategori">
            {/*
              Phase 53.8 — Simplified category dropdown. Operator picks a
              generic bucket (Yüzük, Kolye, Bilezik, …); the system quietly
              resolves it to the best matching legacy category_id via the
              normalization layer so no duplicate categories are created.
              Legacy items with an existing category_id keep it — the
              current value is derived from that ID's canonical slug.
            */}
            <select
              data-testid="inv-category"
              value={form.category_id && categoryById[form.category_id]
                ? resolveCanonicalSlug(categoryById[form.category_id])
                : ""}
              onChange={(e) => {
                const slug = e.target.value;
                if (!slug) { set("category_id", ""); return; }
                const resolvedId = pickBestCategoryId(slug, categories, scope);
                set("category_id", resolvedId || "");
              }}
              className={inputCls}
            >
              <option value="">—</option>
              {CANONICAL_CATEGORIES_METAL.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
            {form.category_id && categoryById[form.category_id] && (
              <p className="text-[10px] text-[#A8A29E] mt-1">Kayıtlı kategori: <b className="text-[#71717A]">{categoryById[form.category_id].name_tr}</b></p>
            )}
          </Field>

          <Field label="Materyal / Ayar">
            <select data-testid="inv-material" value={form.material || ""} onChange={(e) => set("material", e.target.value)} className={inputCls}>
              <option value="">Seçin...</option>
              {/* Phase 52.3 — When the editor is opened from Silver / Gold
                  Inventory, `allowedMaterials` restricts the dropdown to that
                  metal so an operator can't accidentally add a 22K GOLD row
                  while working in Silver Inventory. If the item already has
                  a material outside the scope (edge case: legacy row edited
                  from the wrong screen), we still keep it visible so the
                  current value never disappears. */}
              {(() => {
                const opts = (allowedMaterials && allowedMaterials.length ? allowedMaterials : MATERIAL_OPTIONS).slice();
                if (form.material && !opts.includes(form.material)) opts.unshift(form.material);
                return opts.map((m) => <option key={m} value={m}>{m}</option>);
              })()}
            </select>
          </Field>
          {/* Phase 53.3 — Gold Brand dropdown. Auto-appears when material is
              gold. Options come from Accounting → Markalar (active only).
              Legacy brand_id on the row is preserved even if the brand is
              deactivated by prepending the historical entry. */}
          {form.material && GOLD_MATERIALS_SET.has(form.material) && (
            <Field label="Marka">
              <select data-testid="inv-brand" value={form.brand_id || ""} onChange={(e) => set("brand_id", e.target.value)} className={inputCls}>
                <option value="">Marka yok</option>
                {(() => {
                  const opts = brands.slice();
                  const currentHasBrand = form.brand_id && !opts.find((b) => b.id === form.brand_id);
                  if (currentHasBrand) opts.unshift({ id: form.brand_id, name: `(pasif) ${form.brand_id.slice(0, 8)}…` });
                  return opts.map((b) => <option key={b.id} value={b.id}>{b.name}</option>);
                })()}
              </select>
              {brands.length === 0 && (
                <p className="text-[10.5px] text-[#71717A] mt-1">
                  Henüz marka yok. <Link to="/usm/accounting" className="text-[#C8A96A] hover:underline">Muhasebe → Markalar</Link> bölümünden ekleyin.
                </p>
              )}
            </Field>
          )}
          {/* Phase 54.22 — Gold sub-category selector. Auto-appears when
              material is any gold variant; required. Drives the Gold
              Inventory sidebar filter (14 Ayar / 18 Ayar / 22 Ayar /
              Sarrafiye / Hurda). Legacy gold rows without a value are
              editable — the operator picks one to bring them under a
              sidebar filter bucket. */}
          {form.material && GOLD_MATERIALS_SET.has(form.material) && (
            <Field label="Altın Kategorisi *">
              <select
                data-testid="inv-gold-category"
                value={form.gold_category || ""}
                onChange={(e) => set("gold_category", e.target.value)}
                className={inputCls}
              >
                <option value="">Seçin...</option>
                {GOLD_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {!form.gold_category && (
                <p className="text-[10.5px] text-[#B45309] mt-1">Kategori seçimi zorunludur.</p>
              )}
            </Field>
          )}
          <Field label="Ağırlık (gr)"><input data-testid="inv-weight" type="number" step="0.001" min="0" value={form.weight_grams} onChange={(e) => set("weight_grams", e.target.value)} className={inputCls} /></Field>

          <Field label="Cinsiyet">
            <select data-testid="inv-gender" value={form.gender || ""} onChange={(e) => set("gender", e.target.value)} className={inputCls}>
              <option value="">—</option>
              {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
          {/* Ring size only when category is a ring */}
          {isRing ? (
            <Field label="Yüzük Ölçüsü">
              <input data-testid="inv-ring-size" type="number" min="1" max="99" step="1" value={form.ring_size}
                onChange={(e) => set("ring_size", e.target.value)} className={inputCls} placeholder="örn. 17" />
            </Field>
          ) : <div />}

          <Field label="Durum *">
            <select data-testid="inv-status" value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
              {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label={<PriceLabel base="Satış Fiyatı (TRY)" auto={!!engine && !saleLocked} locked={saleLocked} onLock={unlockSale} onReset={resetSale} testidLock={engine?.testids?.lockSale} testidReset={engine?.testids?.resetSale} />}>
            <input data-testid="inv-sale-price" type="number" step="0.01" min="0" value={form.sale_price_try} onChange={(e) => set("sale_price_try", e.target.value)} placeholder="Boş bırakılabilir" className={inputCls} disabled={!!engine && !saleLocked} />
          </Field>
          {canCost && (
            <Field label={<PriceLabel base="Alış Fiyatı (TRY)" auto={!!engine && !purchaseLocked} locked={purchaseLocked} onLock={unlockPurchase} onReset={resetPurchase} testidLock={engine?.testids?.lockPurchase} testidReset={engine?.testids?.resetPurchase} />}>
              <input data-testid="inv-cost-price" type="number" step="0.01" min="0" value={form.purchase_price_try} onChange={(e) => set("purchase_price_try", e.target.value)} className={inputCls} disabled={!!engine && !purchaseLocked} />
            </Field>
          )}

          {/* Phase 49 — Silver Pricing panel + Phase 54.22 — Gold Pricing
              panel. Rendered only when the material has a registered
              pricing engine (silver: 925/999; gold: 8/14/18/22/24K). For
              every other material this block is completely absent — no
              extra fields, no extra state observers, nothing to affect
              the existing flow. */}
          {engine && !isGoldEngine && (
            <div data-testid={engine.testids.section} className="sm:col-span-2 border border-[#E7E5E4] bg-[#FAFAF9] px-4 py-3 mt-1">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Gümüş Fiyatlandırma</p>
                  <p className="text-[11px] text-[#52525B] mt-0.5">Ağırlık × TCMB USD × Alış USD/g → Alış (TRY). Alış × Çarpan → Satış.</p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#C8A96A]">Otomatik</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="USD Kuru (TCMB)">
                  <input
                    data-testid={engine.testids.usdRate}
                    readOnly
                    value={usdRate != null ? Number(usdRate).toFixed(4) : "…"}
                    className={inputCls + " bg-white cursor-not-allowed"}
                    title="TCMB Forex Selling — otomatik güncellenir"
                  />
                </Field>
                <Field label="Alış (USD / gram)">
                  <input
                    data-testid={engine.testids.purchaseUsd}
                    type="number"
                    step="0.01"
                    min="0"
                    value={silverPurchaseUsd}
                    onChange={(e) => setSilverPurchaseUsd(e.target.value)}
                    placeholder="örn. 5.50"
                    className={inputCls}
                  />
                </Field>
                <Field label="Çarpan (Satış)">
                  <select
                    data-testid={engine.testids.multiplier}
                    value={pricingMultiplier}
                    onChange={(e) => setPricingMultiplier(Number(e.target.value))}
                    className={inputCls}
                  >
                    {engine.multipliers.map((m) => <option key={m} value={m}>{m}x</option>)}
                  </select>
                </Field>
              </div>
              <p className="text-[10px] text-[#71717A] mt-2 leading-relaxed">
                Alış veya Satış&apos;ı elle yazmak için yanındaki <Pencil size={10} className="inline align-middle" /> ikonuna,
                otomatik hesaplamaya dönmek için <RotateCcw size={10} className="inline align-middle" /> ikonuna dokunun.
              </p>
            </div>
          )}

          {/* Phase 54.22 — Gold Pricing panel. Formulas (per user spec):
                Alış (TRY) = ağırlık × (TCMB Gold/TRY ÷ 1000) × Alış Çarpanı
                Satış (TRY) = ağırlık × (TCMB Gold/TRY ÷ 1000) × Satış Çarpanı
              The /1000 conversion is applied SERVER-SIDE so the UI here
              only ever shows the per-gram rate. Buy & Sell multipliers
              are INDEPENDENT (unlike Silver's buy→sell multiplier). */}
          {isGoldEngine && (
            <div data-testid={engine.testids.section} className="sm:col-span-2 border border-[#E7E5E4] bg-[#FAFAF9] px-4 py-3 mt-1">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Altın Fiyatlandırma</p>
                  <p className="text-[11px] text-[#52525B] mt-0.5">Ağırlık × TCMB Altın/TRY × Çarpan → Alış &amp; Satış (TRY). İki çarpan bağımsızdır.</p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#C8A96A]">Otomatik</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="TCMB Altın Kuru (g)">
                  <input
                    data-testid={engine.testids.rate}
                    readOnly
                    value={goldTryPerGram != null ? Number(goldTryPerGram).toFixed(4) : "…"}
                    className={inputCls + " bg-white cursor-not-allowed"}
                    title="TCMB Gold/TRY, gram başına — TCMB senkronizasyonundan otomatik güncellenir"
                  />
                  {goldRateMeta?.updated_at ? (
                    <p className="text-[10px] text-[#71717A] mt-1">
                      TCMB · {new Date(goldRateMeta.updated_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  ) : (
                    <p className="text-[10px] text-[#71717A] mt-1">TCMB kur senkronizasyonu bekleniyor…</p>
                  )}
                </Field>
                <Field label="Alış Çarpanı">
                  <input
                    data-testid={engine.testids.buyMultiplier}
                    type="number"
                    step="0.01"
                    min="0"
                    value={goldBuyMultiplier}
                    onChange={(e) => setGoldBuyMultiplier(e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="örn. 700"
                    className={inputCls}
                  />
                </Field>
                <Field label="Satış Çarpanı">
                  <input
                    data-testid={engine.testids.sellMultiplier}
                    type="number"
                    step="0.01"
                    min="0"
                    value={goldSellMultiplier}
                    onChange={(e) => setGoldSellMultiplier(e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="örn. 800"
                    className={inputCls}
                  />
                </Field>
              </div>
              <p className="text-[10px] text-[#71717A] mt-2 leading-relaxed">
                Alış veya Satış&apos;ı elle yazmak için yanındaki <Pencil size={10} className="inline align-middle" /> ikonuna,
                otomatik hesaplamaya dönmek için <RotateCcw size={10} className="inline align-middle" /> ikonuna dokunun.
              </p>
            </div>
          )}

          <Field label="Stok Adedi *">
            <input data-testid="inv-quantity" type="number" min="0" max="999999" step="1" value={form.quantity ?? 1}
              onChange={(e) => set("quantity", e.target.value)} className={inputCls} placeholder="1" />
          </Field>
          <Field label="Min. Stok Uyarısı">
            <input data-testid="inv-min-quantity" type="number" min="0" max="999999" step="1" value={form.min_quantity ?? 0}
              onChange={(e) => set("min_quantity", e.target.value)} className={inputCls} placeholder="0 = uyarı yok" />
          </Field>

          <Field label="Etiket Notu (opsiyonel)" className="sm:col-span-2">
            <input data-testid="inv-label-note-input" value={form.label_note || ""} onChange={(e) => set("label_note", e.target.value)} maxLength={64} placeholder="Rezerve · Tamir · Müşteri adı · 14K · vb." className={inputCls} />
          </Field>
          <Field label="Notlar" className="sm:col-span-2"><textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className={inputCls + " resize-none"} /></Field>

          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1.5">Görsel</label>
            <div
              className="flex items-start gap-3 flex-wrap p-2 border border-dashed border-transparent hover:border-[#E7E5E4] transition-colors"
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#C8A96A]", "bg-[#FAFAF9]"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("border-[#C8A96A]", "bg-[#FAFAF9]")}
              onDrop={(e) => { e.currentTarget.classList.remove("border-[#C8A96A]", "bg-[#FAFAF9]"); onPhotoDrop(e); }}
            >
              {(form.photo_urls || []).map((u, i) => (
                <div key={i} className="relative">
                  <img src={u} alt="" className="h-20 w-20 object-cover border border-[#E7E5E4]" />
                  <button type="button" onClick={() => set("photo_urls", form.photo_urls.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-black text-white w-5 h-5 text-xs">✕</button>
                </div>
              ))}
              {/* Phase 54.23.7 — Instant local previews of files that are
                  currently being uploaded. Progress bar overlays the
                  thumbnail. When the server URL arrives, the entry
                  disappears from here and appears in form.photo_urls above. */}
              {Object.entries(pendingUploads).map(([cid, up]) => (
                <div key={cid} data-testid="inv-photo-uploading" className="relative">
                  <img src={up.previewUrl} alt="" className="h-20 w-20 object-cover border border-[#E7E5E4] opacity-70" />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
                    <span className="text-[10px] text-white font-mono tabular-nums">{up.progress}%</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 pointer-events-none">
                    <div className="h-full bg-[#C8A96A] transition-[width] duration-100 ease-linear" style={{ width: `${up.progress}%` }} />
                  </div>
                </div>
              ))}
              <label data-testid="inv-photo-picker" className="h-20 w-20 border border-dashed border-[#E7E5E4] flex flex-col items-center justify-center cursor-pointer hover:bg-[#FAFAF9] gap-1">
                <Upload size={16} className="text-[#71717A]" />
                <span className="text-[9px] text-[#71717A] uppercase tracking-wider">Foto Ekle</span>
                <input data-testid="inv-photo-upload" type="file" accept="image/*,.heic,.heif" multiple
                  onChange={(e) => { enqueueUploads(e.target.files); e.target.value = ""; }}
                  className="hidden" />
              </label>
            </div>
            <p className="text-[10px] text-[#71717A] mt-1">JPG, PNG, WEBP, HEIC — sürükle bırak veya tıkla · maks 25 MB · en fazla 5 görsel</p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E7E5E4] flex flex-wrap justify-end items-center gap-3">
          {/* Phase 54.23.7 — subtle pending-upload hint. Save button is
              NOT disabled — the operator can still save without the
              pending photos if they don't want to wait. */}
          {pendingCount > 0 && (
            <span data-testid="inv-pending-uploads-hint" className="text-[11px] text-[#71717A] tracking-wide mr-auto">
              {pendingCount} görsel yükleniyor…
            </span>
          )}
          <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9]">İptal</button>
          {mode === "create" && canPrint && (
            <button data-testid="inv-save-and-print-btn" disabled={!valid} onClick={() => onSave(mergePricingState(form), { printAfter: true })}
              className="bg-[#C8A96A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3 inline-flex items-center gap-2">
              <Printer size={14} /> Kaydet & Etiket Yazdır
            </button>
          )}
          <button data-testid="inv-save-btn" disabled={!valid} onClick={() => onSave(mergePricingState(form))}
            className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// Phase 49 — Field label augmented with lock/reset icons for auto-priced
// fields. When `auto` is true, the price is computed from the engine and the
// input is disabled; clicking the pencil unlocks it for manual entry. When
// `locked` (i.e. user has taken manual control), the pencil shows filled and
// the reset icon (↺) restores automatic mode.
function PriceLabel({ base, auto, locked, onLock, onReset, testidLock, testidReset }) {
  // If there is no engine active, `auto` is false and `locked` is false —
  // render the plain label with no icons at all (legacy behaviour intact).
  if (!auto && !locked) return <span>{base}</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{base}</span>
      {auto && (
        <span className="text-[9px] text-[#C8A96A] font-normal normal-case tracking-normal">· otomatik</span>
      )}
      {locked ? (
        <button
          type="button"
          data-testid={testidReset}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReset?.(); }}
          title="Otomatik hesaplamayı geri yükle"
          className="inline-flex items-center justify-center w-5 h-5 text-[#71717A] hover:text-[#0A0A0A]"
        >
          <RotateCcw size={11} />
        </button>
      ) : (
        <button
          type="button"
          data-testid={testidLock}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLock?.(); }}
          title="Elle düzenle"
          className="inline-flex items-center justify-center w-5 h-5 text-[#71717A] hover:text-[#0A0A0A]"
        >
          <Pencil size={11} />
        </button>
      )}
    </span>
  );
}

// Phase 46 — Feature #9. Column customization popover.
// Renders a checkbox list where each row is draggable to reorder. Persists via
// UsmPrefsContext.setInventoryColumns (MongoDB → cross-device sync).
function ColumnsPicker({ columns, onToggle, onMove, onReset, onClose }) {
  const [dragIdx, setDragIdx] = useState(null);
  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      const el = document.getElementById("inv-cols-picker");
      if (el && !el.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);
  return (
    <div
      id="inv-cols-picker"
      data-testid="inv-cols-picker"
      className="absolute right-0 top-[calc(100%+6px)] z-30 w-72 bg-white border border-[#E7E5E4] shadow-lg p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Sütunları Özelleştir</p>
        <button data-testid="inv-cols-reset" onClick={onReset} className="text-[10px] uppercase tracking-[0.15em] text-[#71717A] hover:text-[#0A0A0A] inline-flex items-center gap-1">
          <RotateCcw size={11} /> Sıfırla
        </button>
      </div>
      <ul className="space-y-0.5 max-h-72 overflow-y-auto">
        {columns.map((c, idx) => (
          <li
            key={c.key}
            data-testid={`inv-col-row-${c.key}`}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIdx != null && dragIdx !== idx) onMove(dragIdx, idx); setDragIdx(null); }}
            onDragEnd={() => setDragIdx(null)}
            className={"flex items-center gap-2 px-2 py-1.5 hover:bg-[#FAFAF9] cursor-move " + (dragIdx === idx ? "opacity-50" : "")}
          >
            <GripVertical size={12} className="text-[#A1A1AA] shrink-0" />
            <input
              type="checkbox"
              data-testid={`inv-col-vis-${c.key}`}
              checked={c.visible}
              onChange={() => onToggle(c.key)}
              className="accent-[#C8A96A] cursor-pointer"
            />
            <span className="text-xs flex-1 truncate">{COLUMN_LABELS[c.key] || c.key}</span>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-[#A1A1AA] mt-2 leading-relaxed">Sürükleyip bırakarak sıralayın. Sağ kenardan sürükleyerek genişliği ayarlayın. Tercihler cihazlar arasında senkronize olur.</p>
    </div>
  );
}

// Column resize handle — sits on the right edge of each <th>. Uses pointer
// events so mouse & touch both work. Width persists via prefs. onChange fires
// on every pointermove (local state); onCommit fires once on pointerup so we
// only PATCH the server one time per drag (not per pixel).
function ColumnResizeHandle({ initialWidth, onChange, onCommit }) {
  const startX = React.useRef(0);
  const startW = React.useRef(0);
  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startX.current = e.clientX;
    startW.current = initialWidth;
    const onMove = (ev) => {
      const delta = ev.clientX - startX.current;
      onChange(startW.current + delta);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onCommit && onCommit();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  return (
    <span
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
      className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-[#C8A96A]/40"
      title="Sütun genişliğini ayarla"
    />
  );
}

// Phase 47 #1 — Floating horizontal scrollbar.
//
// Problem: the inventory table can be 1600–2200px wide with all columns
// visible, but the native browser scrollbar only appears at the BOTTOM of the
// table. On lists with 100+ rows the user must first scroll to the bottom
// just to see rightward columns — bad UX.
//
// Solution: render a slim proxy scrollbar that:
//   • is fixed to the viewport bottom while the table is on-screen
//   • has an inner width equal to the table's real scrollWidth
//   • mirrors and drives the table's scrollLeft in both directions
//   • auto-hides when the table doesn't overflow (no horizontal scroll needed)
//   • auto-hides when the table has scrolled past the top/bottom of viewport
//
// No third-party libs; pure DOM sync. Behaves like Excel / Google Sheets.
function FloatingScrollbar({ scrollRef, innerRef }) {
  const proxyRef = React.useRef(null);
  const spacerRef = React.useRef(null);
  const [visible, setVisible] = React.useState(false);
  const syncingRef = React.useRef(false);
  const [rectLeft, setRectLeft] = React.useState(0);
  const [rectWidth, setRectWidth] = React.useState(0);

  React.useEffect(() => {
    const scroller = scrollRef.current;
    const proxy = proxyRef.current;
    const spacer = spacerRef.current;
    if (!scroller || !proxy || !spacer) return;

    const onTableScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      proxy.scrollLeft = scroller.scrollLeft;
      syncingRef.current = false;
    };
    const onProxyScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      scroller.scrollLeft = proxy.scrollLeft;
      syncingRef.current = false;
    };
    scroller.addEventListener("scroll", onTableScroll, { passive: true });
    proxy.addEventListener("scroll", onProxyScroll, { passive: true });

    let raf = 0;
    const update = () => {
      spacer.style.width = scroller.scrollWidth + "px";
      const overflow = scroller.scrollWidth > scroller.clientWidth + 1;
      const r = scroller.getBoundingClientRect();
      setRectLeft(r.left);
      setRectWidth(r.width);
      const inView = r.bottom > 60 && r.top < window.innerHeight - 16;
      setVisible(overflow && inView);
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(scroller);
    if (innerRef?.current) ro.observe(innerRef.current);
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    const mo = new MutationObserver(scheduleUpdate);
    if (innerRef?.current) mo.observe(innerRef.current, { childList: true, subtree: true, attributes: true });

    return () => {
      scroller.removeEventListener("scroll", onTableScroll);
      proxy.removeEventListener("scroll", onProxyScroll);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      ro.disconnect();
      mo.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [scrollRef, innerRef]);

  return (
    <div
      ref={proxyRef}
      data-testid="inv-floating-hscroll"
      aria-hidden="true"
      style={{
        position: "fixed",
        bottom: 10,
        left: rectLeft,
        width: rectWidth,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity 150ms ease",
        zIndex: 20,
      }}
      className="overflow-x-auto h-3 bg-black/5 border border-[#E7E5E4] rounded-full shadow-sm"
    >
      <div ref={spacerRef} style={{ height: 1, width: 1 }} />
    </div>
  );
}


const inputCls = "w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";

// Phase 50 #7 — Lightweight image preview. Zero dependencies, closes on
// backdrop click, ESC key, and left-swipe (basic pointer gesture). We
// intentionally do NOT provide zoom (spec: "No zoom required") — the point
// is a quick full-image glance, not a photo viewer.
function ImageLightbox({ url, caption, onClose }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Basic swipe-to-close (works on iPad / Android touch).
  const startX = React.useRef(null);
  const onPointerDown = (e) => { startX.current = e.clientX; };
  const onPointerUp = (e) => {
    if (startX.current == null) return;
    const dx = e.clientX - startX.current;
    startX.current = null;
    if (Math.abs(dx) > 80) onClose();
  };
  return (
    <div
      data-testid="inv-image-lightbox"
      onClick={onClose}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      role="button"
      tabIndex={0}
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
    >
      <div className="max-w-[92vw] max-h-[92vh] flex flex-col items-center gap-3">
        <img
          src={url}
          alt={caption || ""}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-[86vh] object-contain shadow-2xl"
        />
        {caption && (
          <div className="text-white/90 text-xs uppercase tracking-[0.2em] text-center">{caption}</div>
        )}
      </div>
    </div>
  );
}

